/**
 * PracticeExam.js - Practice Exam Component
 * Generates 13 MCQs + 2 short coding questions based on selected chapter
 * Questions weighted by struggle heatmap weaknesses (if data available)
 * 20-minute soft timer, hybrid grading (local MCQ + LLM for coding)
 */

import React, { useState, useEffect, useRef } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { API_BASE_URL, apiFetch, isDemoMode } from '../config';
import { getCurriculumUrl } from '../App';
import { getESTTimestamp } from '../utils';

// Helper to render question text with proper code formatting
const FormattedQuestion = ({ text }) => {
  if (!text) return null;
  
  // First check for explicit code blocks (```python ... ```)
  if (text.includes('```')) {
    const parts = text.split(/(```(?:python)?\n?[\s\S]*?```)/g);
    return (
      <>
        {parts.map((part, i) => {
          const codeMatch = part.match(/```(?:python)?\n?([\.\s\S]*?)```/);
          if (codeMatch) {
            // Normalise semicolon-separated statements into newlines for proper display
            const rawCode = codeMatch[1].trim();
            const formattedCode = rawCode.includes('\n') ? rawCode : rawCode.replace(/;\s*/g, '\n');
            return (
              <SyntaxHighlighter
                key={i}
                language="python"
                style={vscDarkPlus}
                customStyle={{
                  margin: '10px 0',
                  padding: '12px 16px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  border: '1px solid #374151'
                }}
                showLineNumbers={false}
              >
                {formattedCode}
              </SyntaxHighlighter>
            );
          }
          // Render inline backtick spans within plain text parts
          const inlineParts = part.split(/(`.+?`)/g);
          return inlineParts.map((seg, k) => {
            if (seg.startsWith('`') && seg.endsWith('`') && seg.length > 2) {
              const inlineCode = seg.slice(1, -1);
              // If inline code looks like multi-statement Python, render as a block
              if (inlineCode.includes(';') && /\b(if|elif|else|for|while|def|class)\b/.test(inlineCode)) {
                const blockCode = inlineCode.replace(/;\s*/g, '\n');
                return (
                  <SyntaxHighlighter
                    key={`${i}-${k}`}
                    language="python"
                    style={vscDarkPlus}
                    customStyle={{
                      margin: '10px 0',
                      padding: '12px 16px',
                      borderRadius: '6px',
                      fontSize: '14px',
                      lineHeight: '1.5',
                      border: '1px solid #374151'
                    }}
                    showLineNumbers={false}
                  >
                    {blockCode}
                  </SyntaxHighlighter>
                );
              }
              // Short inline code — render as styled <code> span
              return <code key={`${i}-${k}`} style={{ backgroundColor: '#1e293b', color: '#93c5fd', padding: '1px 5px', borderRadius: '4px', fontSize: '13px', fontFamily: 'monospace' }}>{inlineCode}</code>;
            }
            return seg.split('\n').map((line, j, arr) => (
              <React.Fragment key={`${i}-${k}-${j}`}>
                {line}
                {j < arr.length - 1 && <br />}
              </React.Fragment>
            ));
          });
        })}
      </>
    );
  }

  // No triple-backtick blocks — check for inline backtick spans
  if (text.includes('`')) {
    const inlineParts = text.split(/(`.+?`)/g);
    const hasMultiStmtInline = inlineParts.some(seg =>
      seg.startsWith('`') && seg.endsWith('`') && seg.includes(';') && /\b(if|elif|else|for|while|def|class)\b/.test(seg)
    );
    return (
      <>
        {inlineParts.map((seg, i) => {
          if (seg.startsWith('`') && seg.endsWith('`') && seg.length > 2) {
            const inlineCode = seg.slice(1, -1);
            if (inlineCode.includes(';') && /\b(if|elif|else|for|while|def|class)\b/.test(inlineCode)) {
              const blockCode = inlineCode.replace(/;\s*/g, '\n');
              return (
                <SyntaxHighlighter
                  key={i}
                  language="python"
                  style={vscDarkPlus}
                  customStyle={{
                    margin: '10px 0',
                    padding: '12px 16px',
                    borderRadius: '6px',
                    fontSize: '14px',
                    lineHeight: '1.5',
                    border: '1px solid #374151'
                  }}
                  showLineNumbers={false}
                >
                  {blockCode}
                </SyntaxHighlighter>
              );
            }
            return <code key={i} style={{ backgroundColor: '#1e293b', color: '#93c5fd', padding: '1px 5px', borderRadius: '4px', fontSize: '13px', fontFamily: 'monospace' }}>{inlineCode}</code>;
          }
          return seg.split('\n').map((line, j, arr) => (
            <React.Fragment key={`${i}-${j}`}>
              {line}
              {j < arr.length - 1 && <br />}
            </React.Fragment>
          ));
        })}
      </>
    );
  }
  
  // No explicit code blocks — try to detect inline code in question text
  // Split into "question intro" and "code part" by finding where code starts
  const lines = text.split('\n');
  const codeKeywords = /^\s*(def |class |if |elif |else:|for |while |print\(|return |import |from |try:|except|with |#|\w+\s*=\s*\S|input\(|range\()/;
  
  let codeStartIdx = -1;
  for (let i = 0; i < lines.length; i++) {
    if (codeKeywords.test(lines[i]) && i > 0) {
      codeStartIdx = i;
      break;
    }
  }
  
  if (codeStartIdx > 0) {
    const textPart = lines.slice(0, codeStartIdx).join('\n');
    const codePart = lines.slice(codeStartIdx).join('\n');
    return (
      <>
        {textPart.split('\n').map((line, j, arr) => (
          <React.Fragment key={`t-${j}`}>
            {line}
            {j < arr.length - 1 && <br />}
          </React.Fragment>
        ))}
        <SyntaxHighlighter
          language="python"
          style={vscDarkPlus}
          customStyle={{
            margin: '10px 0',
            padding: '12px 16px',
            borderRadius: '6px',
            fontSize: '14px',
            lineHeight: '1.5',
            border: '1px solid #374151'
          }}
          showLineNumbers={false}
        >
          {codePart.trim()}
        </SyntaxHighlighter>
      </>
    );
  }
  
  // Fallback: just preserve newlines
  return (
    <>
      {lines.map((line, j) => (
        <React.Fragment key={j}>
          {line}
          {j < lines.length - 1 && <br />}
        </React.Fragment>
      ))}
    </>
  );
};

const PracticeExam = ({ 
  username, 
  syllabusMap, 
  selectedSyllabusDate,
  onClose,
  logAction,
  normalizeSubjectWithLLM,
  masteryAggregate,
  questionAnsweredSubjects,
  selectedSection
}) => {
  // Build class_section string for question bank API calls (e.g. "CIS3260-08-GSU")
  const getClassSection = () => {
    const section = selectedSection || localStorage.getItem('selected_section') || (localStorage.getItem('class_section') || '').split(',')[0].trim();
    const university = localStorage.getItem('university') || '';
    return section && university ? `${section}-${university}` : '';
  };
  // Exam states
  const [examState, setExamState] = useState('setup'); // 'setup', 'generating', 'taking', 'grading', 'results'
  const [selectedChapter, setSelectedChapter] = useState('');
  const [quizMode, setQuizMode] = useState('personalized'); // 'personalized', 'standard', 'custom', or 'similar'
  const [examDifficulty, setExamDifficulty] = useState('mixed'); // 'easy', 'medium', 'hard', 'very hard', 'mixed'
  const [customChapters, setCustomChapters] = useState({}); // { chapterKey: true/false } for custom mode
  const [customSubjectText, setCustomSubjectText] = useState(''); // Free-form subject for 'similar' mode
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({}); // { questionIndex: answer }
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generationStep, setGenerationStep] = useState(0); // Track generation progress
  const [allowedConceptsForGrading, setAllowedConceptsForGrading] = useState([]); // Store for grading phase
  const [extraExplanations, setExtraExplanations] = useState({}); // { idx: text } session-only deeper explanations
  const [explainingIdx, setExplainingIdx] = useState(null); // which question is currently being explained
  
  // Struggle heatmap data
  const [struggleData, setStruggleData] = useState(null); // { topic: { SUCCESS, SYNTAX_ERROR, LOGIC_ERROR, RUNTIME_ERROR } }
  const [weaknessTopics, setWeaknessTopics] = useState([]);
  
  // Timer state
  const [timeRemaining, setTimeRemaining] = useState(20 * 60); // 20 minutes in seconds
  const [timerExpired, setTimerExpired] = useState(false);
  const timerRef = useRef(null);

  // Fetch struggle heatmap data on mount
  useEffect(() => {
    const fetchStruggleData = async () => {
      if (!username) return;
      
      try {
        const response = await apiFetch(
          `${API_BASE_URL}/api/logStudentStruggle?username=${encodeURIComponent(username)}&limit=600`
        );
        if (!response.ok) return;
        
        const result = await response.json();
        const logs = result.struggle_log || [];
        
        // Build grid: { topic: { SUCCESS, SYNTAX_ERROR, LOGIC_ERROR, RUNTIME_ERROR } }
        const grid = {};
        logs.forEach(log => {
          const topic = log.topic || 'other';
          const type = log.struggle_type || 'LOGIC_ERROR';
          
          if (!grid[topic]) grid[topic] = { SUCCESS: 0, SYNTAX_ERROR: 0, LOGIC_ERROR: 0, RUNTIME_ERROR: 0 };
          grid[topic][type]++;
        });
        
        setStruggleData(grid);
        
        // Calculate weakness topics (high error rate)
        const weakTopics = Object.entries(grid)
          .map(([topic, counts]) => {
            const totalErrors = counts.SYNTAX_ERROR + counts.LOGIC_ERROR + counts.RUNTIME_ERROR;
            const total = totalErrors + counts.SUCCESS;
            const errorRate = total > 0 ? totalErrors / total : 0;
            return { topic, errorRate, totalErrors, counts };
          })
          .filter(t => t.totalErrors >= 2) // At least 2 errors to be considered weak
          .sort((a, b) => b.errorRate - a.errorRate) // Highest error rate first
          .slice(0, 8) // Top 8 weakest topics
          .map(t => ({ topic: t.topic, errorRate: Math.round(t.errorRate * 100), counts: t.counts }));
        
        setWeaknessTopics(weakTopics);
      } catch (err) {
        console.error('Failed to fetch struggle data:', err);
      }
    };
    
    fetchStruggleData();
  }, [username]);

  // Prevent background scrolling
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // Timer countdown
  useEffect(() => {
    if (examState === 'taking' && timeRemaining > 0) {
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            setTimerExpired(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [examState, timeRemaining]);

  // Format time display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Format code - handles array of lines (preferred) or string with escape sequences
  const formatCode = (code) => {
    if (!code) return '';
    
    // Best case: code is already an array of lines
    if (Array.isArray(code)) {
      return code.join('\n');
    }
    
    // Fallback: handle string format
    let str = String(code);
    
    // Try to parse as JSON array if it looks like one
    if (str.trim().startsWith('[')) {
      try {
        const parsed = JSON.parse(str);
        if (Array.isArray(parsed)) {
          return parsed.join('\n');
        }
      } catch (e) {
        // Not valid JSON, continue with string processing
      }
    }
    
    // Extract from markdown fences if present
    const fenceMatch = str.match(/```(?:python)?\n?([\s\S]*?)```/);
    if (fenceMatch) {
      str = fenceMatch[1];
    }
    
    // Replace escape sequences
    str = str.replace(/\\n/g, '\n').replace(/\\t/g, '    ');
    
    return str.trim();
  };

  // ==================== QUESTION BANK HELPERS ====================

  // localStorage keys for exclude IDs (per chapter, per type)
  const getExcludeKey = (type) => `pysage_examExclude_${type}`; // mc or wr

  // Get exclude IDs from localStorage
  const getExcludeIds = (type) => {
    try {
      const stored = localStorage.getItem(getExcludeKey(type));
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  };

  // Save exclude IDs to localStorage
  const saveExcludeIds = (type, ids) => {
    try {
      localStorage.setItem(getExcludeKey(type), JSON.stringify(ids));
    } catch (e) { console.warn('Failed to save excludeIds:', e); }
  };

  // Personalized mode: sliding window of past question texts
  const PERSONALIZED_HISTORY_KEY = 'pysage_personalizedExamHistory';
  const MAX_PERSONALIZED_HISTORY = 30;

  const getPersonalizedHistory = () => {
    try {
      const stored = localStorage.getItem(PERSONALIZED_HISTORY_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  };

  const savePersonalizedHistory = (questionTexts) => {
    try {
      let history = getPersonalizedHistory();
      history = [...history, ...questionTexts];
      // Keep only last 30 (sliding window)
      if (history.length > MAX_PERSONALIZED_HISTORY) {
        history = history.slice(-MAX_PERSONALIZED_HISTORY);
      }
      localStorage.setItem(PERSONALIZED_HISTORY_KEY, JSON.stringify(history));
    } catch (e) { console.warn('Failed to save personalized history:', e); }
  };

  // Fetch random questions from question bank API (single chapter)
  const fetchBankQuestions = async (type, chapter, difficulty, limit, excludeIds = []) => {
    const endpoint = type === 'mc' ? 'mcBank' : 'wrBank';
    const response = await apiFetch(`${API_BASE_URL}/api/${endpoint}/random`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        chapter,
        difficulty,
        limit: limit * 3, // request extra in case backend doesn't filter by difficulty
        excludeIds,
        class_section: getClassSection()
      })
    });
    if (!response.ok) {
      const errText = await response.text().catch(() => '');
      throw new Error(`Failed to fetch ${type} ${difficulty} questions: ${response.status} ${errText}`);
    }
    const data = await response.json();
    const all = data.questions || [];
    // Filter client-side to ensure correct difficulty (backend may not filter)
    const exact = all.filter(q => q.difficulty === difficulty);
    return exact.length > 0 ? exact.slice(0, limit) : [];
  };

  // Fetch from ALL chapters, pool results, then randomly pick 'needed' count
  // If not enough at requested difficulty, falls back to nearest difficulties
  const DIFFICULTY_FALLBACK = {
    'easy':      ['easy', 'medium'],
    'medium':    ['medium', 'easy', 'hard'],
    'hard':      ['hard', 'very hard', 'medium'],
    'very hard': ['very hard', 'hard']
  };

  const fetchBankAcrossChapters = async (type, chapterIds, difficulty, needed, excludeIds = []) => {
    const perChapterLimit = Math.max(needed, 4);

    // Try the requested difficulty first
    const promises = chapterIds.map(ch =>
      fetchBankQuestions(type, ch, difficulty, perChapterLimit, excludeIds)
        .catch(err => {
          console.warn(`⚠️ fetchBank ${type}/${ch}/${difficulty} failed:`, err.message);
          return [];
        })
    );
    const results = await Promise.all(promises);
    let pool = results.flat();
    console.log(`📦 fetchBankAcrossChapters: type=${type}, difficulty=${difficulty}, needed=${needed}, pool=${pool.length} from ${chapterIds.length} chapters`);

    // If not enough, try fallback difficulties
    if (pool.length < needed) {
      const fallbacks = (DIFFICULTY_FALLBACK[difficulty] || []).slice(1); // skip first (already tried)
      for (const fallbackDiff of fallbacks) {
        if (pool.length >= needed) break;
        const remaining = needed - pool.length;
        console.log(`📦 Fallback: trying ${type}/${fallbackDiff} for ${remaining} more...`);
        const usedIds = [...excludeIds, ...pool.map(q => q.id).filter(Boolean)];
        const fbPromises = chapterIds.map(ch =>
          fetchBankQuestions(type, ch, fallbackDiff, remaining, usedIds)
            .catch(() => [])
        );
        const fbResults = await Promise.all(fbPromises);
        pool = [...pool, ...fbResults.flat()];
      }
      if (pool.length > needed) {
        // Trim excess from fallback
        pool = pool.slice(0, needed + pool.length); // keep all, will trim after shuffle
      }
    }

    // Shuffle and pick 'needed'
    for (let i = pool.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }
    return pool.slice(0, needed);
  };

  // Cache for course structure (slide ID → title mapping)
  const [courseStructureMap, setCourseStructureMap] = useState({});
  
  // Load course_structure.json on mount to get slide ID → title mapping
  useEffect(() => {
    const loadCourseStructure = async () => {
      try {
        const url = getCurriculumUrl('course_structure.json');
        const resp = await fetch(url);
        if (resp.ok) {
          const data = await resp.json();
          // Build map: { "slide04": "Selections (Conditionals)", ... }
          const map = {};
          data.forEach(item => {
            if (item.id && item.title) {
              map[item.id] = item.title;
            }
          });
          setCourseStructureMap(map);
          console.log('📚 Course structure loaded:', Object.keys(map).length, 'chapters');
        }
      } catch (err) {
        console.warn('Failed to load course_structure.json:', err);
      }
    };
    loadCourseStructure();
  }, []);

  // Helper: Convert slide ID (e.g., "slide04") to chapter title (e.g., "Selections (Conditionals)")
  const slideIdToChapterTitle = (slideId) => {
    if (!slideId) return 'General';
    // First try course_structure.json mapping
    if (courseStructureMap[slideId]) {
      return courseStructureMap[slideId];
    }
    // Fallback: return the slideId as-is if no match
    return slideId;
  };

  // Convert bank MC question to exam format
  const bankMcToExamFormat = (q) => ({
    type: 'mcq',
    question: q.question || q.question_text,
    options: [
      `A) ${q.option_a}`,
      `B) ${q.option_b}`,
      `C) ${q.option_c}`,
      `D) ${q.option_d}`
    ],
    correctAnswer: q.correct_answer,
    topic: slideIdToChapterTitle(q.chapter),
    difficulty: q.difficulty,
    explanation: q.explanation || '',
    _bankId: q.id
  });

  // Convert bank WR question to exam format
  const bankWrToExamFormat = (q) => ({
    type: 'coding',
    question: q.question || q.question_text,
    sampleSolution: q.sample_solution || q.sample_answer || '',
    topic: slideIdToChapterTitle(q.chapter),
    difficulty: q.difficulty,
    _bankId: q.id
  });

  // Generate exam from Question Bank (Standard & Custom modes)
  const generateExamFromBank = async (validChapters) => {
    setGenerationStep(2);
    await new Promise(r => setTimeout(r, 500));

    // Map chapter keys to slide IDs (e.g., "Chapter 4: Selections" -> "slide04")
    const chapterIds = validChapters.map(ch => {
      const fileName = syllabusMap[ch] || '';
      return fileName.replace('.txt', '');
    }).filter(Boolean);

    console.log('📦 Fetching from question bank, chapters:', chapterIds);

    const mcExclude = getExcludeIds('mc');
    const wrExclude = getExcludeIds('wr');

    setGenerationStep(3);

    // Build fetch plan based on examDifficulty
    // mixed = 4 easy + 4 medium + 4 hard MC, 1 easy + 1 medium + 1 hard WR
    // single difficulty = 12 MC + 3 WR all at that difficulty
    let mcFetchPlan, wrFetchPlan;
    if (examDifficulty === 'mixed') {
      mcFetchPlan = [{ diff: 'easy', count: 4 }, { diff: 'medium', count: 4 }, { diff: 'hard', count: 4 }];
      wrFetchPlan = [{ diff: 'easy', count: 1 }, { diff: 'medium', count: 1 }, { diff: 'hard', count: 1 }];
    } else {
      mcFetchPlan = [{ diff: examDifficulty, count: 12 }];
      wrFetchPlan = [{ diff: examDifficulty, count: 3 }];
    }

    const fetchAllWithExcludes = async (mcExcl, wrExcl) => {
      const mcPromises = mcFetchPlan.map(p => fetchBankAcrossChapters('mc', chapterIds, p.diff, p.count, mcExcl));
      const wrPromises = wrFetchPlan.map(p => fetchBankAcrossChapters('wr', chapterIds, p.diff, p.count, wrExcl));
      const results = await Promise.all([...mcPromises, ...wrPromises]);
      const mcResults = results.slice(0, mcFetchPlan.length);
      const wrResults = results.slice(mcFetchPlan.length);
      return { mcResults, wrResults };
    };

    let mcResults, wrResults;
    try {
      ({ mcResults, wrResults } = await fetchAllWithExcludes(mcExclude, wrExclude));

      // If too few questions came back, reset excludes and retry (pool exhausted)
      const totalMc = mcResults.reduce((sum, arr) => sum + arr.length, 0);
      const totalWr = wrResults.reduce((sum, arr) => sum + arr.length, 0);
      if (totalMc + totalWr < 5 && (mcExclude.length > 0 || wrExclude.length > 0)) {
        console.warn(`⚠️ Only got ${totalMc} MC + ${totalWr} WR with ${mcExclude.length + wrExclude.length} excludes — resetting and retrying...`);
        saveExcludeIds('mc', []);
        saveExcludeIds('wr', []);
        ({ mcResults, wrResults } = await fetchAllWithExcludes([], []));
      }
    } catch (err) {
      console.warn('⚠️ Bank fetch failed, retrying without excludeIds...', err.message);
      saveExcludeIds('mc', []);
      saveExcludeIds('wr', []);
      ({ mcResults, wrResults } = await fetchAllWithExcludes([], []));
    }

    setGenerationStep(4);
    await new Promise(r => setTimeout(r, 400));

    // Deduplicate: parallel fetches + fallback can return the same question
    const dedup = (arr) => {
      const seen = new Set();
      return arr.filter(q => {
        const key = q.id || q.question || JSON.stringify(q);
        if (seen.has(key)) {
          console.warn(`⚠️ Duplicate question removed: ${key}`);
          return false;
        }
        seen.add(key);
        return true;
      });
    };

    // Convert to exam format
    const allMcRaw = dedup(mcResults.flat());
    const allWrRaw = dedup(wrResults.flat());
    const mcQuestions = allMcRaw.map(bankMcToExamFormat);
    const wrQuestions = allWrRaw.map(bankWrToExamFormat);
    const allQuestions = [...mcQuestions, ...wrQuestions];

    if (allQuestions.length < 5) {
      throw new Error(`Not enough questions in the bank. Found ${mcQuestions.length} MC and ${wrQuestions.length} WR. Please ask your instructor to generate more questions.`);
    }

    // Save used IDs to exclude list
    const usedMcIds = allMcRaw.map(q => q.id).filter(Boolean);
    const usedWrIds = allWrRaw.map(q => q.id).filter(Boolean);
    saveExcludeIds('mc', [...mcExclude, ...usedMcIds]);
    saveExcludeIds('wr', [...wrExclude, ...usedWrIds]);

    console.log(`📦 Bank exam: ${mcQuestions.length} MC + ${wrQuestions.length} WR = ${allQuestions.length} total`);
    return allQuestions;
  };

  // Generate exam questions using LLM
  const generateExam = async () => {
    // Validation based on mode
    if (quizMode === 'custom') {
      const selectedCustomChapters = Object.keys(customChapters).filter(k => customChapters[k]);
      if (selectedCustomChapters.length === 0) {
        setError('Please select at least one chapter for custom mode');
        return;
      }
    } else if (quizMode === 'similar') {
      if (!customSubjectText.trim()) {
        setError('Please enter a subject or topic to generate questions about');
        return;
      }
      if (!selectedChapter) {
        setError('Please select a chapter scope first');
        return;
      }
    } else if (!selectedChapter) {
      setError('Please select a chapter first');
      return;
    }

    setLoading(true);
    setError(null);
    setExamState('generating');
    setGenerationStep(1);
    
    // Log exam started
    const chapterInfo = quizMode === 'custom' 
      ? `Custom: ${Object.keys(customChapters).filter(k => customChapters[k]).map(k => (syllabusMap[k] || k).replace('.txt', '')).join(', ')}`
      : quizMode === 'similar'
      ? `Similar: ${customSubjectText.trim().slice(0, 80)} (scope: ${selectedChapter === '__nolimit__' ? 'no limit' : selectedChapter})`
      : (syllabusMap[selectedChapter] || selectedChapter).replace('.txt', '');
    logAction?.('Practice Exam Started', `Mode: ${quizMode}, Difficulty: ${examDifficulty}, Chapter: ${chapterInfo}`, '', null, 'Practice Exam');

    try {
      // Step 1: Analyzing your learning history
      await new Promise(r => setTimeout(r, 800));
      setGenerationStep(2);

      // ========== SIMILAR: LLM-generated from a free-form subject (with chapter scope) ==========
      if (quizMode === 'similar') {
        const subjectText = customSubjectText.trim();
        const noScopeLimit = selectedChapter === '__nolimit__';

        // Build valid chapters list: all chapters up to and including selectedChapter (skipped if no limit)
        const allChaptersSim = Object.keys(syllabusMap).sort();
        const selectedIdxSim = noScopeLimit ? -1 : allChaptersSim.indexOf(selectedChapter);
        const validChaptersSim = noScopeLimit ? [] : allChaptersSim.slice(0, selectedIdxSim + 1);
        const validChapterListSim = noScopeLimit ? '' : validChaptersSim.map(ch => syllabusMap[ch]?.replace('.txt', '') || ch).join(', ');

        // Load valid chapter contents (used by both scope check AND main generation prompt).
        // Skipped entirely when 'No knowledge limit' is selected — nothing about course content is sent.
        let allChapterContentsSim = '';
        if (!noScopeLimit) {
          for (const ch of validChaptersSim) {
            try {
              const fileName = syllabusMap[ch];
              const resp = await fetch(getCurriculumUrl(fileName));
              if (resp.ok) {
                const text = await resp.text();
                allChapterContentsSim += `\n\n--- ${fileName} ---\n${text}`;
              }
            } catch (e) { /* skip */ }
          }
        }

        // ---- Scope-check: ask the LLM whether the subject fits within the actual chapter content ----
        // Skipped when 'No knowledge limit' is selected (there is no scope to check against).
        if (!noScopeLimit) {
          try {
          const scopeCheckResp = await apiFetch(`${API_BASE_URL}/api/chat`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              model: 'gpt-4o-mini',
              messages: [
                {
                  role: 'system',
                  content: `You decide whether a Python practice subject fits the topics a student has studied. The chapter content you receive is a brief OUTLINE (topic headings, concept lists, allowed syntax) — NOT a full lesson. Treat the outline as a topic indicator: if the subject belongs to the same topic area as one of the chapters, it fits — even if specific sub-details (parameters, instance variables, edge cases, etc.) are not literally listed. Only return fits=false when the subject clearly belongs to a topic area that is NOT in any of the chapter outlines (e.g., the subject is about file I/O but no chapter mentions files; the subject is about classes but no chapter mentions classes). Default to {"fits": true} whenever the subject's topic area is represented in the outlines. Reply with strict JSON only: {"fits": true} or {"fits": false, "note": "<one short sentence naming the missing TOPIC AREA and suggesting the student pick a different subject or expand their chapter scope>"}. No markdown, no extra keys.`
                },
                {
                  role: 'user',
                  content: `Chapters covered so far: ${validChapterListSim}\n\n=== CHAPTER CONTENT (everything the student has studied) ===\n${allChapterContentsSim.slice(0, 8000)}\n=== END ===\n\nStudent's subject/topic for the practice exam:\n"""${subjectText}"""\n\nLook at the chapter content above. Are the concepts this subject needs mentioned or demonstrated there? Reply with JSON only.`
                }
              ],
              temperature: 0,
              max_tokens: 100
            })
          });
          if (scopeCheckResp.ok) {
            const scopeData = await scopeCheckResp.json();
            let scopeRaw = scopeData.choices?.[0]?.message?.content || '';
            scopeRaw = scopeRaw.replace(/```json\n?|\n?```/g, '').trim();
            const scopeJson = JSON.parse(scopeRaw);
            if (scopeJson && scopeJson.fits === false) {
              const note = scopeJson.note || 'This subject requires concepts beyond your current chapter scope.';
              logAction?.('Practice Exam Blocked', `Out-of-scope subject: ${subjectText.slice(0, 60)} | scope: ${selectedChapter} | reason: ${note}`, '', null, 'Practice Exam');
              setError(`⚠️ ${note} Please pick a different subject or expand your chapter scope.`);
              setLoading(false);
              setExamState('setup');
              setGenerationStep(0);
              return;
            }
          }
          } catch (scopeErr) {
            // Non-blocking on network/parse errors: fall through and let the existing soft fallback in the main
            // generation prompt handle scope. We only block when the LLM explicitly says fits=false.
            console.warn('Scope check skipped:', scopeErr);
          }
        }

        const difficultyInstruction = examDifficulty === 'mixed'
          ? '- 2 EASY MCQs, 2 MEDIUM MCQs, 2 HARD MCQs\n  - Coding Q7 MEDIUM (5-6 lines)'
          : `- All 6 MCQs at "${examDifficulty}" difficulty\n  - The 1 coding question at "${examDifficulty}" difficulty`;

        const similarPrompt = `You are generating a Python practice exam of exactly 7 questions. The student has provided the following input, and your job is to produce questions HIGHLY SIMILAR to it${noScopeLimit ? '.' : ', using ONLY concepts that appear in the chapter content provided below.'}

=== STUDENT INPUT ===
${subjectText}
=== END INPUT ===
${noScopeLimit ? '\n✅ NO KNOWLEDGE LIMIT: The student has selected no chapter scope. You may use any standard Python concept appropriate for the subject.\n' : `\nCHAPTER SCOPE: The student has only studied up to: ${selectedChapter}\nChapters covered: ${validChapterListSim}\n${allChapterContentsSim ? `\n=== CHAPTER CONTENT (this is the entire universe of concepts the student knows) ===\n${allChapterContentsSim.slice(0, 8000)}\n=== END CHAPTER CONTENT ===\n\nIf the student's subject naturally fits the chapter content, generate questions on it directly. If the subject mentions an advanced technique not present in the chapters, base the questions on the closest related concept that IS in the chapters.` : ''}\n`}

INTERPRETATION RULES:
- If the input looks like ONE OR MORE actual questions (has a "?", code block, "write a program", multiple-choice options, etc.), treat each as a TEMPLATE. Generate new questions that share the same:
    * concept being tested
    * structure (MCQ vs coding, # of options, presence of code snippet)
    * difficulty level
    * style of phrasing
  Then VARY the surface: change variable names, numbers, scenarios, data values, the specific bug, the expected output. Do NOT copy the original verbatim.
- If the input is just a topic/concept name (e.g. "list comprehensions", "recursion"), generate a fresh exam covering that concept broadly.

Structure:
- 6 multiple choice questions (MCQ) FIRST (indices 0-5)
- 1 coding question LAST (index 6)

Difficulty:
${difficultyInstruction}

REQUIREMENTS:
1. Every question MUST test the same KIND of skill as the input.
2. If the input is a question, at least 4 of the 6 MCQs should be close structural variants of it.
3. NEVER reproduce the input question word-for-word. Always vary names, values, or scenario.
4. MCQs have 4 options labeled "A) ...", "B) ...", "C) ...", "D) ..." with exactly ONE correct answer (return just the letter, e.g. "A").
5. The coding question must be solvable in plain Python.
6. Each question MUST include a "topic" field — a short concept name (used for mastery tracking).
7. When a question contains Python code, ALWAYS wrap it in a triple-backtick block with the python tag and use REAL NEWLINES between statements — never semicolons, never inline single backticks for multi-line code. Format exactly like this: \`\`\`python\\nx = 20\\nif x < 10:\\n    print('small')\\n\`\`\`

Return ONLY a valid JSON array (no markdown, no explanation) with this exact structure:
[
  {
    "type": "mcq",
    "question": "What is ...?",
    "options": ["A) ...", "B) ...", "C) ...", "D) ..."],
    "correctAnswer": "A",
    "topic": "<short concept name>",
    "difficulty": "easy"
  },
  {
    "type": "coding",
    "question": "Write a program that ...",
    "sampleSolution": "print('hello')",
    "topic": "<short concept name>",
    "difficulty": "easy"
  }
]`;

        setGenerationStep(3);
        await new Promise(r => setTimeout(r, 400));

        const simResp = await apiFetch(`${API_BASE_URL}/api/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            model: 'gpt-4o-mini',
            messages: [
              {
                role: 'system',
                content: 'You are a Python instructor who creates practice exam questions that closely mirror a student-provided sample question or topic. When chapter content is provided, treat it as the universe of concepts the student knows; when no chapter content is provided, you may use any standard Python concept. Produce structural variants that test the same concept with different specifics. Return ONLY valid JSON arrays, no markdown formatting, no code blocks, no explanations.'
              },
              { role: 'user', content: similarPrompt }
            ],
            temperature: 0.7,
            max_tokens: 2500
          })
        });

        if (!simResp.ok) {
          const errorData = await simResp.json().catch(() => ({}));
          console.error('API Error (similar):', simResp.status, errorData);
          throw new Error(`API Error: ${simResp.status} - ${errorData.error?.message || 'Unknown error'}`);
        }

        const simData = await simResp.json();
        setGenerationStep(4);
        let simContent = simData.choices[0]?.message?.content || '';
        simContent = simContent.replace(/```json\n?|\n?```/g, '').trim();

        let simQuestions;
        try {
          simQuestions = JSON.parse(simContent);
        } catch (parseErr) {
          console.error('JSON Parse Error (similar):', parseErr, 'Content:', simContent);
          throw new Error('Failed to parse exam questions');
        }

        if (!Array.isArray(simQuestions) || simQuestions.length < 5) {
          console.error('Invalid questions array (similar):', simQuestions);
          throw new Error(`Invalid exam format: got ${simQuestions?.length || 0} questions`);
        }

        // Allowed concepts for grading: the topics the LLM actually generated + the subject text
        const simTopics = [...new Set(simQuestions.map(q => q.topic).filter(Boolean))];
        setAllowedConceptsForGrading([...new Set([
          subjectText.toLowerCase(),
          ...simTopics.map(t => t.toLowerCase())
        ])]);

        setGenerationStep(5);
        await new Promise(r => setTimeout(r, 500));

        setQuestions(simQuestions);
        setAnswers({});
        setTimeRemaining(10 * 60);
        setTimerExpired(false);
        setGenerationStep(6);
        await new Promise(r => setTimeout(r, 400));
        setExamState('taking');

        logAction?.('Practice Exam Generated', `${simQuestions.length} questions (similar: ${subjectText.slice(0, 60)}, scope: ${selectedChapter})`, '', null, 'Practice Exam');
        return;
      }

      // Get list of all chapters in order
      const allChapters = Object.keys(syllabusMap).sort();
      
      // Determine valid chapters based on quiz mode
      let validChapters;
      if (quizMode === 'custom') {
        // Custom mode: use only the chapters the student selected
        validChapters = allChapters.filter(ch => customChapters[ch]);
      } else {
        // Standard/Personalized: use chapters up to selected chapter
        const selectedChapterIndex = allChapters.indexOf(selectedChapter);
        validChapters = allChapters.slice(0, selectedChapterIndex + 1);
      }

      // ========== STANDARD & CUSTOM: Use Question Bank API ==========
      if (quizMode === 'standard' || quizMode === 'custom') {
        const generatedQuestions = await generateExamFromBank(validChapters);
        
        setGenerationStep(5);
        await new Promise(r => setTimeout(r, 600));

        // Build allowedConcepts from the bank questions' topics + valid chapter names
        const bankTopics = [...new Set(generatedQuestions.map(q => q.topic).filter(Boolean))];
        const bankAllowedConcepts = ['variables', 'print()', 'input()', 'arithmetic', 'strings', ...bankTopics];
        // Also add chapter titles as allowed concepts
        validChapters.forEach(ch => {
          const cleaned = ch.replace(/^Chapter\s*\d+:\s*/i, '').trim();
          if (cleaned) bankAllowedConcepts.push(cleaned.toLowerCase());
        });
        setAllowedConceptsForGrading([...new Set(bankAllowedConcepts)]);

        setQuestions(generatedQuestions);
        setAnswers({});
        setTimeRemaining(20 * 60);
        setTimerExpired(false);
        setGenerationStep(6);
        await new Promise(r => setTimeout(r, 400));
        setExamState('taking');
        
        logAction?.('Practice Exam Generated', `${generatedQuestions.length} questions (from bank)`, '', null, 'Practice Exam');
        return;
      }

      // ========== PERSONALIZED: Continue with LLM generation ==========
      const validChapterNames = validChapters.map(ch => {
        const fileName = syllabusMap[ch] || '';
        return fileName.replace('slide', '').replace('.txt', '').toLowerCase();
      });
      
      // Build a list of ALLOWED topics based on chapter content
      // Load all valid chapter contents to extract topic names
      let allowedTopicsFromContent = [];
      for (const ch of validChapters) {
        try {
          const fileName = syllabusMap[ch];
          const resp = await fetch(getCurriculumUrl(fileName));
          if (resp.ok) {
            const text = await resp.text();
            // Extract topic headers from the chapter content
            const topicMatches = text.match(/##\s*[^#\n]+/g) || [];
            topicMatches.forEach(t => {
              const cleaned = t.replace(/##\s*/, '').trim().toLowerCase();
              allowedTopicsFromContent.push(cleaned);
            });
          }
        } catch (e) {
          // Skip if can't load
        }
      }
      
      // Filter weakness topics - only include topics that appear in valid chapters
      // Check if topic name matches any allowed topic (case-insensitive partial match)
      const filteredWeaknessTopics = weaknessTopics.filter(w => {
        const topicLower = w.topic.toLowerCase();
        // Check against extracted topic names
        const matchesContent = allowedTopicsFromContent.some(allowed => 
          allowed.includes(topicLower) || topicLower.includes(allowed)
        );
        // Also check against chapter names
        const matchesChapter = validChapterNames.some(ch => 
          topicLower.includes(ch) || ch.includes(topicLower)
        );
        // Allow basic Python topics that are always valid in early chapters
        // NOTE: Do NOT include lists, functions, dictionaries here - they may be future chapters
        const isBasicTopic = ['variables', 'print', 'input', 'operators', 'strings', 'numbers', 'data types', 'arithmetic', 'assignment', 'comments'].some(basic => 
          topicLower.includes(basic)
        );
        return matchesContent || matchesChapter || isBasicTopic;
      });
      
      console.log('Valid chapters:', validChapters);
      console.log('Quiz mode:', quizMode);
      console.log('Original weakness topics:', weaknessTopics.map(w => w.topic));
      console.log('Filtered weakness topics:', filteredWeaknessTopics.map(w => w.topic));
      
      // Build emphasis instruction — only personalized mode reaches here
      let emphasisInstruction = '';
      
      if (filteredWeaknessTopics.length >= 4) {
        // PERSONALIZED MODE: Use heatmap weaknesses
        const weaknessDetails = filteredWeaknessTopics.slice(0, 5).map((w, i) => {
          const errorTypes = [];
          if (w.counts.SYNTAX_ERROR > 0) errorTypes.push(`${w.counts.SYNTAX_ERROR} syntax errors`);
          if (w.counts.LOGIC_ERROR > 0) errorTypes.push(`${w.counts.LOGIC_ERROR} logic errors`);
          if (w.counts.RUNTIME_ERROR > 0) errorTypes.push(`${w.counts.RUNTIME_ERROR} runtime errors`);
          return `${i + 1}. ${w.topic} (${w.errorRate}% error rate: ${errorTypes.join(', ')})`;
        }).join('\n');
        
        emphasisInstruction = `
🎯 PERSONALIZED MODE - Based on student's struggle heatmap weaknesses:
${weaknessDetails}

QUESTION DISTRIBUTION REQUIREMENTS:
- At least 7 of the 12 MCQs MUST focus on these weak topics (they are ALL within chapter scope)
- At least 2 of the 3 coding questions MUST focus on the student's weak topics: "${filteredWeaknessTopics[0]?.topic}", "${filteredWeaknessTopics[1]?.topic || filteredWeaknessTopics[0]?.topic}", or "${filteredWeaknessTopics[2]?.topic || filteredWeaknessTopics[0]?.topic}"
- The other coding question should combine/mix concepts from the current chapter AND previous chapters
- For topics with SYNTAX errors, include questions about proper syntax.
- For topics with LOGIC errors, include questions that test understanding of how code flows.
- For topics with RUNTIME errors, include questions about edge cases and error handling.`;
      } else {
        // Personalized but not enough weakness data — emphasis on recent chapters
        emphasisInstruction = `
📚 PERSONALIZED MODE (limited weakness data) - Emphasis on recent chapters:

QUESTION DISTRIBUTION:
- MORE questions from RECENT chapters (current + last 2-3 chapters)
- FEWER questions from earlier chapters
- The most recent material should be tested more heavily
- Cover a variety of topics but prioritize what was learned recently
- Coding questions should test practical application of recent concepts`;
      }

      // Build sliding window of past personalized questions to avoid repeats
      const personalizedHistory = getPersonalizedHistory();
      let avoidRepeatSection = '';
      if (personalizedHistory.length > 0) {
        avoidRepeatSection = `\n\n🚫 PREVIOUSLY ASKED QUESTIONS — Do NOT repeat or closely rephrase any of these:\n${personalizedHistory.map((q, i) => `${i + 1}. ${q}`).join('\n')}\n\nGenerate completely NEW and DIFFERENT questions.`;
      }

      // Load ALL valid chapter contents (current + previous)
      let allChapterContents = '';
      const loadedChapters = []; // DEBUG: Track loaded chapters
      
      console.log('🔍 DEBUG [Practice Exam - Chapter Loading]:', {
        quizMode,
        validChapters,
        selectedChapter
      });
      
      for (const ch of validChapters) {
        try {
          const fileName = syllabusMap[ch];
          const url = getCurriculumUrl(fileName);
          console.log(`📄 DEBUG [Practice Exam]: Loading ${fileName} from ${url}`);
          const resp = await fetch(url);
          if (resp.ok) {
            const text = await resp.text();
            allChapterContents += `\n\n=== ${fileName} ===\n${text}`;
            loadedChapters.push(fileName);
          } else {
            console.error(`❌ DEBUG [Practice Exam]: Failed to load ${fileName} - Status: ${resp.status}`);
          }
        } catch (err) {
          console.log(`Could not load chapter ${ch}:`, err);
        }
      }
      
      console.log('✅ DEBUG [Practice Exam - Loading Complete]:', {
        chaptersLoaded: loadedChapters,
        totalChaptersLoaded: loadedChapters.length,
        contentLengthBeforeTruncate: allChapterContents.length
      });

      // Dynamically detect which concepts are NOT in the loaded chapters
      // We check for TEACHING indicators, not just word mentions
      const contentLower = allChapterContents.toLowerCase();
      const forbiddenConcepts = [];
      
      // Check if concepts are actually TAUGHT (not just mentioned)
      // Look for teaching patterns like "## Functions" or "defining functions" or code examples "def "
      const hasFunctions = contentLower.includes('def ') || 
                           contentLower.includes('defining function') || 
                           contentLower.includes('## function') ||
                           contentLower.includes('return value');
      const hasLoops = contentLower.includes('for ') || 
                       contentLower.includes('while ') || 
                       contentLower.includes('## loop') ||
                       contentLower.includes('range(');
      const hasLists = contentLower.includes('append(') || 
                       contentLower.includes('## list') ||
                       contentLower.includes('[0]') ||
                       contentLower.includes('list[');
      const hasConditionals = contentLower.includes('if ') || 
                              contentLower.includes('## selection') ||
                              contentLower.includes('## conditional');
      const hasDictionaries = contentLower.includes('## dict') || 
                              contentLower.includes("['key']") ||
                              contentLower.includes('key-value');
      const hasClasses = contentLower.includes('class ') || 
                         contentLower.includes('__init__') ||
                         contentLower.includes('## object');
      const hasExceptions = contentLower.includes('try:') || 
                            contentLower.includes('except ') ||
                            contentLower.includes('## exception');
      const hasFiles = contentLower.includes('open(') || 
                       contentLower.includes('.read(') ||
                       contentLower.includes('## file');

      if (!hasFunctions) {
        forbiddenConcepts.push('- Functions: NO def, NO return, NO function definitions, NO "Create a function"');
      }
      if (!hasLoops) {
        forbiddenConcepts.push('- Loops: NO for, NO while, NO range(), NO iteration');
      }
      if (!hasLists) {
        forbiddenConcepts.push('- Lists: NO [], NO append(), NO list indexing, NO list operations');
      }
      if (!hasConditionals) {
        forbiddenConcepts.push('- Conditionals: NO if, NO else, NO elif');
      }
      if (!hasDictionaries) {
        forbiddenConcepts.push('- Dictionaries: NO {}, NO key-value pairs');
      }
      if (!hasClasses) {
        forbiddenConcepts.push('- Classes: NO class definitions, NO __init__, NO OOP');
      }
      if (!hasExceptions) {
        forbiddenConcepts.push('- Exception handling: NO try, NO except');
      }
      if (!hasFiles) {
        forbiddenConcepts.push('- File handling: NO open(), NO file read/write');
      }

      // Build allowed concepts list dynamically
      const allowedConcepts = ['variables', 'print()', 'input()', 'arithmetic', 'strings'];
      if (hasConditionals) allowedConcepts.push('if/else');
      if (hasLoops) allowedConcepts.push('loops', 'range()');
      if (hasFunctions) allowedConcepts.push('functions', 'def', 'return');
      if (hasLists) allowedConcepts.push('lists', '[]');
      if (hasDictionaries) allowedConcepts.push('dictionaries', '{}');

      const forbiddenSection = forbiddenConcepts.length > 0 
        ? `\n🚫🚫🚫 ABSOLUTELY FORBIDDEN CONCEPTS - VIOLATION WILL FAIL THE TASK 🚫🚫🚫
${forbiddenConcepts.join('\n')}

✅ ALLOWED CONCEPTS (ONLY use these): ${allowedConcepts.join(', ')}

Any question using a FORBIDDEN concept will be rejected. Double-check each question before including it.
`
        : '';

      console.log('🚫 DEBUG [Practice Exam - Forbidden Concepts]:', forbiddenConcepts);
      console.log('✅ DEBUG [Practice Exam - Allowed Concepts]:', allowedConcepts);
      
      // Save allowed concepts for grading phase
      setAllowedConceptsForGrading(allowedConcepts);
      
      // Truncate if too long (keep most recent chapters' content prioritized)
      const maxContentLength = 8000;
      if (allChapterContents.length > maxContentLength) {
        console.log(`⚠️ DEBUG [Practice Exam]: Content truncated from ${allChapterContents.length} to ${maxContentLength} chars`);
        allChapterContents = allChapterContents.slice(-maxContentLength);
      }

      // Build explicit list of valid chapters for the prompt
      const validChapterList = validChapters.map(ch => syllabusMap[ch]?.replace('.txt', '') || ch).join(', ');

      // Only personalized mode reaches here
      const scopeDescription = `The student has ONLY learned up to: ${selectedChapter}
ALLOWED chapters/slides: ${validChapterList}`;

      const prompt = `Generate a Python practice exam with exactly 15 questions:
- 12 multiple choice questions (MCQ) - MUST BE FIRST in the array (questions 1-12)
  * 4 EASY MCQs (difficulty: "easy")
  * 4 MEDIUM MCQs (difficulty: "medium")
  * 4 HARD MCQs (difficulty: "hard")
- 3 coding questions - MUST BE LAST in the array (questions 13-15):
  * Question 13: EASY coding (3-4 lines, difficulty: "easy")
  * Question 14: MEDIUM coding (5-6 lines, difficulty: "medium")
  * Question 15: HARD coding (7-8 lines, combining multiple concepts, difficulty: "hard")

⚠️ CRITICAL CHAPTER SCOPE RESTRICTION ⚠️
${scopeDescription}
${forbiddenSection}
ONLY use concepts that appear in the chapter content below. Read it carefully.

${allChapterContents ? `\n=== ALL ALLOWED CHAPTER CONTENT (ONLY use topics from here) ===\n${allChapterContents}` : ''}

${emphasisInstruction}
${avoidRepeatSection}

STRICT REQUIREMENTS:
1. MCQs should have 4 options (A, B, C, D) with only ONE correct answer
2. Coding questions must be solvable using ONLY concepts from the chapters above - NO FUNCTIONS if not taught
3. VERIFY each question only uses topics found in the chapter content above
4. DO NOT use topics from later chapters in questions OR in explanations
5. Difficulty distribution: 4 easy MCQ, 4 medium MCQ, 4 hard MCQ + 1 easy WR, 1 medium WR, 1 hard WR
6. Each question must have a clear correct answer
7. ORDER: All 12 MCQs FIRST (indices 0-11), then 3 coding questions LAST (indices 12-14)
8. IMPORTANT: When a question contains Python code snippets, wrap the code in triple backticks with python language tag inside the question string, like: "What is the output of the following code?\n\n\`\`\`python\nx = 5\nif x > 3:\n    print('yes')\n\`\`\`"

Return ONLY a valid JSON array with this exact structure (no markdown, no explanation):
[
  {
    "type": "mcq",
    "question": "What is the output of print(2 + 3)?",
    "options": ["A) 5", "B) 23", "C) Error", "D) None"],
    "correctAnswer": "A",
    "topic": "Operators",
    "difficulty": "easy"
  },
  {
    "type": "coding",
    "question": "Write a program that prints 'Hello World'.",
    "sampleSolution": "print('Hello World')",
    "topic": "Output",
    "difficulty": "easy"
  },
  {
    "type": "coding",
    "question": "Write a program that asks the user for two numbers and prints their sum.",
    "sampleSolution": "num1 = float(input('Enter first number: '))\\nnum2 = float(input('Enter second number: '))\\nresult = num1 + num2\\nprint('Sum:', result)",
    "topic": "Input/Output",
    "difficulty": "medium"
  },
  {
    "type": "coding",
    "question": "Write a program that asks for a number and prints whether it is positive, negative, or zero, and whether it is even or odd.",
    "sampleSolution": "num = int(input('Enter a number: '))\\nif num > 0:\\n    print('Positive')\\nelif num < 0:\\n    print('Negative')\\nelse:\\n    print('Zero')\\nif num % 2 == 0:\\n    print('Even')\\nelse:\\n    print('Odd')",
    "topic": "Conditionals",
    "difficulty": "hard"
  }
]`;

      // Step 3: Creating personalized questions
      setGenerationStep(3);
      await new Promise(r => setTimeout(r, 500));

      const response = await apiFetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            {
              role: 'system',
              content: 'You are a Python instructor creating practice exam questions. You MUST strictly follow chapter scope restrictions. Return ONLY valid JSON arrays, no markdown formatting, no code blocks, no explanations.'
            },
            { role: 'user', content: prompt }
          ],
          temperature: 0.7,
          max_tokens: 4000
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('API Error:', response.status, errorData);
        throw new Error(`API Error: ${response.status} - ${errorData.error?.message || 'Unknown error'}`);
      }

      const data = await response.json();
      // Step 4: Receiving questions from AI
      setGenerationStep(4);
      
      console.log('LLM Response:', data);
      let content = data.choices[0]?.message?.content || '';
      
      // Clean up response - remove markdown code blocks if present
      content = content.replace(/```json\n?|\n?```/g, '').trim();
      
      console.log('Cleaned content:', content.substring(0, 200));
      
      let generatedQuestions;
      try {
        generatedQuestions = JSON.parse(content);
      } catch (parseErr) {
        console.error('JSON Parse Error:', parseErr, 'Content:', content);
        throw new Error('Failed to parse exam questions');
      }
      
      if (!Array.isArray(generatedQuestions) || generatedQuestions.length < 10) {
        console.error('Invalid questions array:', generatedQuestions);
        throw new Error(`Invalid exam format: got ${generatedQuestions?.length || 0} questions`);
      }

      // Step 5: Finalizing your exam
      setGenerationStep(5);
      await new Promise(r => setTimeout(r, 600));

      // Save question texts to personalized history (sliding window)
      const questionTexts = generatedQuestions.map(q => q.question || '').filter(Boolean);
      savePersonalizedHistory(questionTexts);

      setQuestions(generatedQuestions);
      setAnswers({});
      setTimeRemaining(20 * 60);
      setTimerExpired(false);
      setGenerationStep(6);
      await new Promise(r => setTimeout(r, 400));
      setExamState('taking');
      
      // Log exam generated
      logAction?.('Practice Exam Generated', `${generatedQuestions.length} questions`, '', null, 'Practice Exam');

    } catch (err) {
      console.error('Exam generation error:', err);
      
      // Show a helpful, student-friendly error message
      const msg = err.message || '';
      if (msg.includes('Not enough questions')) {
        setError(`😕 Not enough questions available for this chapter and difficulty. Try selecting a different difficulty level, or choose more chapters in Custom mode.`);
      } else if (msg.includes('Failed to fetch')) {
        setError(`📡 Could not connect to the question bank. Please check your internet connection and try again.`);
      } else if (msg.includes('API Error') || msg.includes('parse')) {
        setError(`⚠️ Something went wrong generating the exam. Please try again in a moment.`);
      } else {
        setError(`😕 No questions found for this selection. Try a different chapter, difficulty, or quiz mode.`);
      }
      setExamState('setup');
    } finally {
      setLoading(false);
    }
  };

  // Handle answer selection for MCQ
  const handleMCQAnswer = (questionIndex, answer) => {
    setAnswers(prev => ({ ...prev, [questionIndex]: answer }));
  };

  // Handle code answer for coding questions
  const handleCodeAnswer = (questionIndex, code) => {
    setAnswers(prev => ({ ...prev, [questionIndex]: code }));
  };

  // Save MC question results to quiz APIs after grading
  // This updates: Past Quiz Review, Knowledge Health, Quiz Analytics
  const saveMCQuizResults = async (gradingResults) => {
    if (!gradingResults?.results || !questions) return;
    
    console.log('📊 Saving MC quiz results to APIs...');
    console.log('📚 courseStructureMap at save time:', courseStructureMap);
    
    // Only process MCQ questions (first 12 questions, indices 0-11)
    const mcQuestions = questions.filter(q => q.type === 'mcq');
    
    for (let i = 0; i < mcQuestions.length; i++) {
      const question = mcQuestions[i];
      const questionIndex = questions.indexOf(question);
      
      // Find the grading result for this question
      const gradingResult = gradingResults.results.find(r => r.questionIndex === questionIndex) 
        || gradingResults.results[i]; // Fallback to array position
      
      if (!gradingResult) {
        console.warn(`⚠️ No grading result found for MC question ${i}`);
        continue;
      }
      
      const isCorrect = gradingResult.correct;
      const studentAnswer = answers[questionIndex] || '';
      
      // Get topic - convert slide ID to title using courseStructureMap if available
      let rawTopic = question.topic || 'General';
      // If topic looks like a slide ID (e.g., "slide07"), convert it to title
      if (rawTopic.startsWith('slide') && courseStructureMap[rawTopic]) {
        rawTopic = courseStructureMap[rawTopic];
        console.log(`📚 Converted ${question.topic} → ${rawTopic}`);
      }
      
      // Normalize subject using LLM matching (same as regular quizzes)
      let normalizedSubject = rawTopic;
      if (normalizeSubjectWithLLM) {
        try {
          normalizedSubject = await normalizeSubjectWithLLM(rawTopic, question.question);
        } catch (err) {
          console.warn('Subject normalization failed, using original:', rawTopic);
        }
      }
      
      const timestamp = getESTTimestamp();
      
      // API 1: Save to quiz-result (for Past Quiz Review)
      try {
        // Build full question text with answer options included
        const optionsText = (question.options || []).join('\n');
        const fullQuestion = `${question.question}\n\n${optionsText}\n\nCorrect Answer: ${question.correctAnswer || ''}`;
        
        const quizPayload = {
          username: username || localStorage.getItem('username') || 'anonymous',
          subject: normalizedSubject,
          result: isCorrect ? 'correct' : 'wrong',
          question: fullQuestion,
          user_answer: studentAnswer,
          feedback: gradingResult.explanation || ''
        };
        
        apiFetch(`${API_BASE_URL}/api/quiz-result`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(quizPayload)
        }).then(res => {
          if (res.ok) {
            console.log(`📡 Quiz result saved for: ${normalizedSubject}`);
          }
        }).catch(err => console.error('Error saving quiz result:', err));
      } catch (err) {
        console.error('Error preparing quiz-result:', err);
      }
      
      // API 2: Save to studentMastery (aggregate mastery - for Knowledge Health)
      try {
        apiFetch(`${API_BASE_URL}/api/studentMastery`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            username: username || localStorage.getItem('username') || 'anonymous', 
            subject: normalizedSubject, 
            correct: isCorrect ? 1 : 0, 
            wrong: isCorrect ? 0 : 1, 
            record_at: timestamp 
          })
        }).then(res => {
          if (res.ok) {
            console.log(`📡 Mastery aggregate updated for: ${normalizedSubject}`);
          }
        }).catch(err => console.error('Error saving mastery aggregate:', err));
      } catch (err) {
        console.error('Error preparing studentMastery:', err);
      }
      
      // API 3: Save to studentMasteryHistoryQuestionAnswer (for Quiz Analytics trends)
      try {
        const historyPayload = {
          username: username || localStorage.getItem('username') || 'anonymous',
          subject: normalizedSubject,
          correct: isCorrect ? 1 : 0,
          wrong: isCorrect ? 0 : 1,
          recorded_at: timestamp,
          question: question.question,
          answer: studentAnswer
        };
        
        apiFetch(`${API_BASE_URL}/api/studentMasteryHistoryQuestionAnswer`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(historyPayload)
        }).then(res => {
          if (res.ok) {
            console.log(`📡 Mastery history saved for: ${normalizedSubject}`);
          }
        }).catch(err => console.error('Error saving mastery history:', err));
      } catch (err) {
        console.error('Error preparing masteryHistory:', err);
      }
    }
    
    console.log(`📊 Finished saving ${mcQuestions.length} MC quiz results to APIs`);
  };

  // Submit and grade exam
  const submitExam = async () => {
    setExamState('grading');
    setLoading(true);
    
    // Log exam submitted
    const answeredCount = Object.keys(answers).length;
    logAction?.('Practice Exam Submitted', `Answered: ${answeredCount}/${questions.length}`, '', null, 'Practice Exam');

    try {
      // ========== STEP 1: Grade MCQs locally (instant, free) ==========
      const mcResults = [];
      let mcScore = 0;
      
      questions.forEach((q, idx) => {
        if (q.type !== 'mcq') return;
        
        const studentAnswer = (answers[idx] || '').trim().toUpperCase();
        const correctAnswer = (q.correctAnswer || '').trim().toUpperCase();
        
        // Simple string comparison — extract just the letter (e.g., "A" from "A) print()")
        const studentLetter = studentAnswer.charAt(0);
        const correctLetter = correctAnswer.charAt(0);
        const isCorrect = studentLetter === correctLetter && studentLetter !== '';
        
        if (isCorrect) mcScore += 1;
        
        mcResults.push({
          questionIndex: idx,
          correct: isCorrect,
          pointsEarned: isCorrect ? 1 : 0,
          explanation: isCorrect 
            ? `✅ Correct! ${q.explanation || ''}` 
            : `${q.explanation || `The correct answer is ${q.correctAnswer}.`}`,
          correctAnswer: q.correctAnswer
        });
      });
      
      console.log(`📝 MCQ grading complete: ${mcScore}/${mcResults.length} correct (local, instant)`);
      
      // ========== STEP 2: Grade WR/Coding questions via LLM ==========
      const codingQuestions = questions
        .map((q, idx) => ({ ...q, originalIndex: idx, studentAnswer: answers[idx] || '' }))
        .filter(q => q.type === 'coding');
      
      let wrResults = [];
      let wrScore = 0;
      let wrSummary = '';
      let wrRecommendations = [];
      
      if (codingQuestions.length > 0) {
        const wrPrompt = `Grade these ${codingQuestions.length} Python coding questions. For each question, evaluate the student's answer and provide feedback.

IMPORTANT: This exam is scoped to ${selectedChapter || 'the selected chapters'}. 
The student has ONLY learned these concepts: ${allowedConceptsForGrading.join(', ')}
Keep ALL explanations simple and only reference concepts the student knows.
Do NOT mention concepts they haven't learned yet in your explanations or sample solutions.

CODING SUBMISSIONS:
${JSON.stringify(codingQuestions.map((q, i) => ({
  index: i,
  originalIndex: q.originalIndex,
  question: q.question,
  sampleSolution: q.sampleSolution,
  studentAnswer: q.studentAnswer,
  difficulty: q.difficulty
})), null, 2)}

For each coding question, determine:
1. Points earned - Grade LENIENTLY on a scale of 0, 0.25, 0.5, 0.75, or 1:
   * 1.0 = Fully correct (minor formatting differences are OK)
   * 0.75 = Correct logic with minor issues (e.g., missing a print, small typo, slightly different variable names)
   * 0.5 = Shows understanding of the core concept but has some errors
   * 0.25 = Partially on the right track but significant issues
   * 0 = Completely wrong, blank, or unrelated
2. Brief explanation of why correct/incorrect - KEEP IT SIMPLE
3. Focus on whether the LOGIC and APPROACH are correct, not exact variable names or formatting
4. Use the "sampleSolution" from the question as the "correctAnswer" - format as array of strings (one per line)

Return ONLY a valid JSON object:
{
  "results": [
    {
      "originalIndex": number,
      "correct": boolean,
      "pointsEarned": number,
      "explanation": "string",
      "correctAnswer": ["line1", "line2"]
    }
  ],
  "summary": "Overall feedback on coding performance",
  "recommendations": ["Study topic X", "Practice Y"]
}`;

        const response = await apiFetch(`${API_BASE_URL}/api/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            model: 'gpt-4.1',
            messages: [
              {
                role: 'system',
                content: 'You are a Python instructor grading coding questions only. Be encouraging and grade LENIENTLY — focus on whether the student understands the concept, not perfect syntax. Give generous partial credit for coding questions that show the right approach or logic even if implementation has minor issues. Return ONLY valid JSON, no markdown. For coding questions, use the sampleSolution provided - do not make up different solutions.'
              },
              { role: 'user', content: wrPrompt }
            ],
            temperature: 0.1,
            max_tokens: 2000
          })
        });

        if (!response.ok) throw new Error('Failed to grade coding questions');

        const data = await response.json();
        let content = data.choices[0]?.message?.content || '';
        content = content.replace(/```json\n?|\n?```/g, '').trim();
        
        content = content.replace(/"correctAnswer"\s*:\s*"([^"]+)"/g, (match, code) => {
          const fixedCode = code.replace(/\\n/g, '\n').replace(/\\t/g, '    ');
          return `"correctAnswer": ${JSON.stringify(fixedCode)}`;
        });
        
        const wrGradingResults = JSON.parse(content);
        
        // Post-process WR results
        if (wrGradingResults.results) {
          wrResults = wrGradingResults.results.map(r => {
            if (r.correctAnswer && typeof r.correctAnswer === 'string' && r.correctAnswer.length > 20) {
              r.correctAnswer = r.correctAnswer.split('\\n').join('\n').split('\\t').join('    ');
            }
            if (Array.isArray(r.correctAnswer)) {
              r.correctAnswer = r.correctAnswer.join('\n');
            }
            // Map back to original question index
            r.questionIndex = r.originalIndex;
            wrScore += (r.pointsEarned || 0);
            return r;
          });
        }
        
        // Capture summary and recommendations from LLM
        wrSummary = wrGradingResults.summary || '';
        wrRecommendations = wrGradingResults.recommendations || [];
        
        console.log(`🤖 WR grading complete: ${wrScore}/${codingQuestions.length} points (LLM)`);
      }
      
      // ========== STEP 3: Generate MC recommendations locally ==========
      const mcRecommendations = [];
      const topicStats = {};
      
      mcResults.forEach((r, i) => {
        const topic = questions[r.questionIndex]?.topic || 'General';
        if (!topicStats[topic]) topicStats[topic] = { correct: 0, total: 0 };
        topicStats[topic].total += 1;
        if (r.correct) topicStats[topic].correct += 1;
      });
      
      Object.entries(topicStats).forEach(([topic, stats]) => {
        const wrongCount = stats.total - stats.correct;
        if (wrongCount > 0) {
          if (stats.correct === 0) {
            mcRecommendations.push(`📖 Review "${topic}" — you missed all ${stats.total} question(s) on this topic`);
          } else if (wrongCount >= stats.total / 2) {
            mcRecommendations.push(`📖 Practice "${topic}" — scored ${stats.correct}/${stats.total} on this topic`);
          }
        }
      });
      
      if (mcResults.length > 0 && mcScore === mcResults.length) {
        mcRecommendations.push('🌟 Perfect on multiple choice — great conceptual understanding!');
      }
      
      // ========== STEP 4: Merge results ==========
      const allResults = [...mcResults, ...wrResults].sort((a, b) => a.questionIndex - b.questionIndex);
      const totalScore = mcScore + wrScore;
      const maxScore = questions.length; // 12 MC + 3 WR = 15
      const percentage = Math.round((totalScore / maxScore) * 100);
      
      // Build summary
      const mcPercent = mcResults.length > 0 ? Math.round((mcScore / mcResults.length) * 100) : 0;
      const summaryParts = [`Multiple Choice: ${mcScore}/${mcResults.length} (${mcPercent}%)`];
      if (codingQuestions.length > 0) {
        summaryParts.push(`Coding: ${wrScore}/${codingQuestions.length} points`);
      }
      if (wrSummary) summaryParts.push(wrSummary);
      
      // Merge MC + WR recommendations
      const allRecommendations = [...mcRecommendations, ...wrRecommendations];
      
      const gradingResults = {
        totalScore,
        maxScore,
        percentage,
        results: allResults,
        summary: summaryParts.join(' · '),
        recommendations: allRecommendations
      };
      
      console.log(`📊 Final score: ${totalScore}/${maxScore} (${percentage}%) — MCQs graded locally, WR by LLM`);
      
      setResults(gradingResults);
      setExamState('results');
      
      // Log exam completed with score
      logAction?.('Practice Exam Completed', `Score: ${gradingResults.totalScore}/${gradingResults.maxScore} (${gradingResults.percentage}%)`, '', null, 'Practice Exam');

      // Save MC question results to quiz APIs (for Past Quiz Review, Knowledge Health, Quiz Analytics)
      await saveMCQuizResults(gradingResults);

      // Award exam points based on percentage grade
      const examUsername = username || localStorage.getItem('username');
      if (examUsername && gradingResults.percentage != null) {
        apiFetch(`${API_BASE_URL}/api/points/award`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username: examUsername,
            actionType: 'EXAM',
            subject: selectedChapter || 'Practice Exam',
            grade: Math.round(gradingResults.percentage)
          })
        }).then(r => r.ok ? r.json() : null)
          .then(d => { if (d) console.log(`🏆 Exam points awarded: +${d.pointsAwarded} for grade ${gradingResults.percentage}%`); })
          .catch(e => console.warn('🏆 Exam points error:', e));
      }

    } catch (err) {
      console.error('Grading error:', err);
      setError('Failed to grade exam. Please try again.');
      setExamState('taking');
    } finally {
      setLoading(false);
    }
  };

  // Render setup screen
  const renderSetup = () => (
    <div style={{ padding: '20px', textAlign: 'center' }}>
      <h2 style={{ color: '#00d4aa', marginBottom: '20px' }}>📝 Practice Exam</h2>
      
      <p style={{ color: '#9ca3af', marginBottom: '30px' }}>
        {quizMode === 'similar'
          ? 'This practice exam will generate 6 multiple choice questions and 1 coding question.'
          : 'This practice exam will generate 12 multiple choice questions and 3 coding questions.'}
      </p>

      {/* Quiz Mode Selection - FIRST */}
      <div style={{ marginBottom: '25px' }}>
        <label style={{ color: '#fef3c7', display: 'block', marginBottom: '15px', fontWeight: 'bold' }}>
          1. Select Quiz Mode:
        </label>
        <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
          {/* Personalized Mode */}
          <button
            onClick={() => setQuizMode('personalized')}
            disabled={weaknessTopics.length === 0}
            style={{
              padding: '15px 20px',
              borderRadius: '12px',
              border: '1px solid rgba(254, 243, 199, 0.45)',
              backgroundColor: quizMode === 'personalized' ? 'rgba(251, 191, 36, 0.2)' : 'rgba(16, 185, 129, 0.04)',
              color: weaknessTopics.length === 0 ? '#6b7280' : '#fff',
              cursor: weaknessTopics.length === 0 ? 'not-allowed' : 'pointer',
              minWidth: '180px',
              textAlign: 'left',
              opacity: weaknessTopics.length === 0 ? 0.5 : 1
            }}
          >
            <div style={{ fontSize: '18px', marginBottom: '5px' }}>🎯 Personalized</div>
            <div style={{ fontSize: '11px', color: '#9ca3af' }}>
              Focus on your weak areas
            </div>
            {weaknessTopics.length === 0 && (
              <div style={{ fontSize: '10px', color: '#ef4444', marginTop: '5px' }}>
                (No struggle data yet - practice coding first!)
              </div>
            )}
          </button>
          
          {/* Standard Mode */}
          <button
            onClick={() => setQuizMode('standard')}
            style={{
              padding: '15px 20px',
              borderRadius: '12px',
              border: '1px solid rgba(254, 243, 199, 0.45)',
              backgroundColor: quizMode === 'standard' ? 'rgba(96, 165, 250, 0.2)' : 'rgba(16, 185, 129, 0.04)',
              color: '#fff',
              cursor: 'pointer',
              minWidth: '180px',
              textAlign: 'left'
            }}
          >
            <div style={{ fontSize: '18px', marginBottom: '5px' }}>📚 Standard</div>
            <div style={{ fontSize: '11px', color: '#9ca3af' }}>
              Emphasis on recent chapters
            </div>
          </button>
          
          {/* Custom Mode */}
          <button
            onClick={() => setQuizMode('custom')}
            style={{
              padding: '15px 20px',
              borderRadius: '12px',
              border: '1px solid rgba(254, 243, 199, 0.45)',
              backgroundColor: quizMode === 'custom' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.04)',
              color: '#fff',
              cursor: 'pointer',
              minWidth: '180px',
              textAlign: 'left'
            }}
          >
            <div style={{ fontSize: '18px', marginBottom: '5px' }}>✏️ Custom</div>
            <div style={{ fontSize: '11px', color: '#9ca3af' }}>
              Pick specific chapters
            </div>
          </button>

          {/* Similar Question/Subject Mode */}
          <button
            onClick={() => setQuizMode('similar')}
            style={{
              padding: '15px 20px',
              borderRadius: '12px',
              border: '1px solid rgba(254, 243, 199, 0.45)',
              backgroundColor: quizMode === 'similar' ? 'rgba(168, 85, 247, 0.2)' : 'rgba(16, 185, 129, 0.04)',
              color: '#fff',
              cursor: 'pointer',
              minWidth: '180px',
              textAlign: 'left'
            }}
          >
            <div style={{ fontSize: '18px', marginBottom: '5px' }}>🧩 Similar Question/Subject</div>
            <div style={{ fontSize: '11px', color: '#9ca3af' }}>
              Generate from any topic you type
            </div>
          </button>
        </div>
      </div>

      {/* Chapter Selection - Personalized, Standard, and Similar modes */}
      {quizMode !== 'custom' && (
        <div style={{ marginBottom: '25px' }}>
          <label style={{ color: '#fef3c7', display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
            2. Select Chapter Scope:
          </label>
          <select
            value={selectedChapter}
            onChange={(e) => setSelectedChapter(e.target.value)}
            style={{
              padding: '12px 20px',
              fontSize: '16px',
              borderRadius: '8px',
              border: '2px solid #374151',
              backgroundColor: '#1f2937',
              color: '#fff',
              minWidth: '250px',
              cursor: 'pointer'
            }}
          >
            <option value="">-- Select a Chapter --</option>
            {quizMode === 'similar' && (
              <option value="__nolimit__">No knowledge limit</option>
            )}
            {Object.entries(syllabusMap).map(([date, fileName]) => (
              <option key={date} value={date}>
                Up to {fileName.replace('slide', 'Slide ').replace('.txt', '')}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Show weakness topics only in personalized mode */}
      {quizMode === 'personalized' && weaknessTopics.length > 0 && (
        <div style={{ 
          backgroundColor: 'rgba(251, 191, 36, 0.1)', 
          border: '1px solid rgba(251, 191, 36, 0.3)',
          borderRadius: '8px',
          padding: '15px',
          marginBottom: '25px',
          textAlign: 'left'
        }}>
          <h4 style={{ color: '#fbbf24', margin: '0 0 10px 0' }}>🎯 Your Weak Areas (questions will focus on these):</h4>
          <div style={{ margin: 0, color: '#fbbf24' }}>
            {weaknessTopics.slice(0, 5).map((w, idx) => (
              <div key={idx} style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontWeight: 'bold' }}>{w.topic}</span>
                <span style={{ 
                  fontSize: '12px', 
                  backgroundColor: w.errorRate > 60 ? 'rgba(239, 68, 68, 0.3)' : 'rgba(251, 191, 36, 0.3)',
                  padding: '2px 8px',
                  borderRadius: '4px'
                }}>
                  {w.errorRate}% error rate
                </span>
                <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                  ({w.counts.SYNTAX_ERROR > 0 ? `${w.counts.SYNTAX_ERROR} syntax, ` : ''}
                  {w.counts.LOGIC_ERROR > 0 ? `${w.counts.LOGIC_ERROR} logic, ` : ''}
                  {w.counts.RUNTIME_ERROR > 0 ? `${w.counts.RUNTIME_ERROR} runtime` : ''})
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Standard mode info */}
      {quizMode === 'standard' && (
        <div style={{ 
          backgroundColor: 'rgba(96, 165, 250, 0.1)', 
          border: '1px solid rgba(96, 165, 250, 0.3)',
          borderRadius: '8px',
          padding: '15px',
          marginBottom: '25px',
          textAlign: 'left'
        }}>
          <h4 style={{ color: '#60a5fa', margin: '0 0 10px 0' }}>📚 Standard Mode:</h4>
          <ul style={{ margin: 0, paddingLeft: '20px', color: '#60a5fa', fontSize: '14px' }}>
            <li>More questions from recent chapters</li>
            <li>Fewer questions from earlier chapters</li>
            <li>Good variety of topics</li>
          </ul>
        </div>
      )}

      {/* Difficulty Selection — only for Standard, Custom, and Similar modes */}
      {(quizMode === 'standard' || quizMode === 'custom' || quizMode === 'similar') && (
        <div style={{ marginBottom: '25px' }}>
          <label style={{ color: '#fef3c7', display: 'block', marginBottom: '12px', fontWeight: 'bold' }}>
            {quizMode === 'standard' ? '3' : '2'}. Select Difficulty:
          </label>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
            {[
              { value: 'easy', label: '😊 Easy', color: '#34d399' },
              { value: 'medium', label: '📚 Medium', color: '#fbbf24' },
              { value: 'hard', label: '🔥 Hard', color: '#f97316' },
              { value: 'very hard', label: '💀 Very Hard', color: '#ef4444' },
              { value: 'mixed', label: '🎲 Mixed', color: '#60a5fa' }
            ].map(opt => (
              <button
                key={opt.value}
                onClick={() => setExamDifficulty(opt.value)}
                style={{
                  padding: '10px 16px',
                  borderRadius: '10px',
                  border: '1px solid rgba(254, 243, 199, 0.45)',
                  backgroundColor: examDifficulty === opt.value ? `${opt.color}22` : 'rgba(16, 185, 129, 0.02)',
                  color: examDifficulty === opt.value ? opt.color : '#9ca3af',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: examDifficulty === opt.value ? 'bold' : 'normal',
                  transition: 'all 0.2s',
                  minWidth: '110px'
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <p style={{ color: '#9ca3af', fontSize: '11px', marginTop: '8px', marginBottom: 0 }}>
            {examDifficulty === 'mixed'
              ? 'Mixed: 4 easy + 4 medium + 4 hard MC · 1 easy + 1 medium + 1 hard WR'
              : `All 12 MC and 3 WR will be ${examDifficulty}`}
          </p>
        </div>
      )}

      {/* Custom mode - chapter checklist */}
      {quizMode === 'custom' && (
        <div style={{ 
          backgroundColor: 'rgba(16, 185, 129, 0.1)', 
          border: '1px solid rgba(16, 185, 129, 0.3)',
          borderRadius: '8px',
          padding: '15px',
          marginBottom: '25px',
          textAlign: 'left'
        }}>
          <h4 style={{ color: '#10b981', margin: '0 0 15px 0' }}>3. ✏️ Select Chapters to Include:</h4>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
            gap: '10px' 
          }}>
            {Object.entries(syllabusMap).map(([date, fileName]) => {
              const isSelected = customChapters[date] || false;
              const chapterName = fileName.replace('slide', 'Slide ').replace('.txt', '');
              return (
                <label
                  key={date}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    backgroundColor: isSelected ? 'rgba(16, 185, 129, 0.3)' : '#1f2937',
                    border: isSelected ? '2px solid #10b981' : '2px solid #374151',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => setCustomChapters({
                      ...customChapters,
                      [date]: e.target.checked
                    })}
                    style={{ 
                      width: '18px', 
                      height: '18px',
                      accentColor: '#10b981'
                    }}
                  />
                  <span style={{ color: '#fef3c7', fontSize: '14px' }}>{chapterName}</span>
                </label>
              );
            })}
          </div>
          <div style={{ marginTop: '15px', display: 'flex', gap: '10px' }}>
            <button
              onClick={() => {
                const all = {};
                Object.keys(syllabusMap).forEach(k => all[k] = true);
                setCustomChapters(all);
              }}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                backgroundColor: '#374151',
                color: '#10b981',
                border: '1px solid #10b981',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              Select All
            </button>
            <button
              onClick={() => setCustomChapters({})}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                backgroundColor: '#374151',
                color: '#9ca3af',
                border: '1px solid #4b5563',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              Clear All
            </button>
          </div>
          <p style={{ color: '#9ca3af', fontSize: '12px', marginTop: '10px', marginBottom: 0 }}>
            Selected: {Object.values(customChapters).filter(Boolean).length} chapter(s)
          </p>
        </div>
      )}

      {/* Similar mode - subject input */}
      {quizMode === 'similar' && (
        <div style={{ 
          backgroundColor: 'rgba(168, 85, 247, 0.1)', 
          border: '1px solid rgba(168, 85, 247, 0.3)',
          borderRadius: '8px',
          padding: '15px',
          marginBottom: '25px',
          textAlign: 'left'
        }}>
          <h4 style={{ color: '#fef3c7', margin: '0 0 10px 0' }}>🧩 Subject / Topic:</h4>
          <p style={{ color: '#9ca3af', fontSize: '12px', marginTop: 0, marginBottom: '10px' }}>
            Enter a Python topic, concept, or paste a sample question. The AI will generate 6 MCQs + 1 coding question on this subject.
          </p>
          <textarea
            value={customSubjectText}
            onChange={(e) => setCustomSubjectText(e.target.value)}
            placeholder="e.g. 'list comprehensions', 'recursion with base cases', or paste a sample question you want similar ones to..."
            style={{
              width: '100%',
              minHeight: '90px',
              padding: '10px 12px',
              borderRadius: '8px',
              border: '2px solid #374151',
              backgroundColor: '#1f2937',
              color: '#fff',
              fontSize: '14px',
              fontFamily: 'inherit',
              resize: 'vertical',
              boxSizing: 'border-box'
            }}
          />
          <p style={{ color: '#9ca3af', fontSize: '11px', marginTop: '6px', marginBottom: 0 }}>
            {customSubjectText.trim().length} characters
          </p>
        </div>
      )}

      <div style={{ 
        backgroundColor: 'rgba(59, 130, 246, 0.1)', 
        border: '1px solid rgba(59, 130, 246, 0.3)',
        borderRadius: '8px',
        padding: '15px',
        marginBottom: '30px'
      }}>
        <p style={{ color: '#60a5fa', margin: 0 }}>
          ⏱️ Time Limit: <strong>{quizMode === 'similar' ? '10 minutes' : '20 minutes'}</strong> (soft limit - you can continue after time expires)
        </p>
      </div>

      {error && (
        <div style={{ color: '#ef4444', marginBottom: '20px' }}>{error}</div>
      )}

      <button
        onClick={generateExam}
        disabled={loading || (quizMode !== 'custom' && !selectedChapter) || (quizMode === 'custom' && Object.values(customChapters).filter(Boolean).length === 0) || (quizMode === 'similar' && !customSubjectText.trim())}
        style={{
          padding: '15px 40px',
          fontSize: '18px',
          backgroundColor: loading ? '#374151' : '#059669',
          color: '#ffffff',
          border: '1px solid rgba(254, 243, 199, 1)',
          borderRadius: '8px',
          fontWeight: 700,
          letterSpacing: '0.03em',
          cursor: loading || (quizMode !== 'custom' && !selectedChapter) || (quizMode === 'custom' && Object.values(customChapters).filter(Boolean).length === 0) || (quizMode === 'similar' && !customSubjectText.trim()) ? 'not-allowed' : 'pointer',
          opacity: loading || (quizMode !== 'custom' && !selectedChapter) || (quizMode === 'custom' && Object.values(customChapters).filter(Boolean).length === 0) || (quizMode === 'similar' && !customSubjectText.trim()) ? 0.6 : 1
        }}
      >
        {loading ? '⏳ Generating Exam...' : '🚀 Start Practice Exam'}
      </button>
    </div>
  );

  // Render exciting generation progress screen
  const renderGenerating = () => {
    const steps = [
      { id: 1, icon: '🔍', text: 'Analyzing your learning history...', subtext: 'Reviewing your struggle patterns' },
      { id: 2, icon: '🎯', text: 'Identifying your weak areas...', subtext: 'Finding topics that need practice' },
      { id: 3, icon: '⚡', text: 'Creating personalized questions...', subtext: 'AI is crafting your unique exam' },
      { id: 4, icon: '📝', text: 'Generating 12 MCQs + 3 coding questions...', subtext: 'Easy, medium, and hard levels' },
      { id: 5, icon: '✨', text: 'Finalizing your practice exam...', subtext: 'Almost ready!' },
      { id: 6, icon: '🚀', text: 'Launching exam...', subtext: 'Get ready!' }
    ];

    return (
      <div style={{ 
        padding: '40px 20px', 
        textAlign: 'center',
        minHeight: '500px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {/* Animated header */}
        <div style={{ 
          fontSize: '60px', 
          marginBottom: '10px',
          animation: 'bounce 1s ease-in-out infinite'
        }}>
          {steps[generationStep - 1]?.icon || '📝'}
        </div>
        
        <h2 style={{ 
          color: '#00d4aa', 
          marginBottom: '10px',
          fontSize: '24px'
        }}>
          Preparing Your Practice Exam
        </h2>
        
        <p style={{ color: '#9ca3af', marginBottom: '40px' }}>
          This exam is being personalized just for you!
        </p>

        {/* Progress steps */}
        <div style={{ 
          width: '100%', 
          maxWidth: '400px',
          textAlign: 'left'
        }}>
          {steps.map((step) => {
            const isActive = generationStep === step.id;
            const isComplete = generationStep > step.id;
            const isPending = generationStep < step.id;
            
            return (
              <div 
                key={step.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '15px',
                  padding: '12px 15px',
                  marginBottom: '8px',
                  backgroundColor: isActive ? 'rgba(59, 130, 246, 0.2)' : isComplete ? 'rgba(16, 185, 129, 0.1)' : 'rgba(55, 65, 81, 0.3)',
                  borderRadius: '10px',
                  border: isActive ? '2px solid #3b82f6' : '2px solid transparent',
                  transition: 'all 0.3s ease',
                  opacity: isPending ? 0.4 : 1
                }}
              >
                {/* Status indicator */}
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: isComplete ? '#10b981' : isActive ? '#3b82f6' : '#374151',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  transition: 'all 0.3s ease'
                }}>
                  {isComplete ? '✓' : isActive ? (
                    <span style={{ animation: 'spin 1s linear infinite' }}>⟳</span>
                  ) : step.id}
                </div>
                
                {/* Step text */}
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    color: isActive ? '#fff' : isComplete ? '#10b981' : '#9ca3af',
                    fontWeight: isActive ? 'bold' : 'normal',
                    fontSize: '14px'
                  }}>
                    {step.text}
                  </div>
                  {isActive && (
                    <div style={{ 
                      color: '#60a5fa', 
                      fontSize: '12px',
                      marginTop: '2px'
                    }}>
                      {step.subtext}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Fun fact while waiting */}
        <div style={{
          marginTop: '30px',
          padding: '15px 20px',
          backgroundColor: 'rgba(251, 191, 36, 0.1)',
          border: '1px solid rgba(251, 191, 36, 0.3)',
          borderRadius: '10px',
          maxWidth: '400px'
        }}>
          <p style={{ color: '#fbbf24', margin: 0, fontSize: '14px' }}>
            💡 <strong>Did you know?</strong> Practice exams that target your weak areas can improve learning retention by up to 40%!
          </p>
        </div>
      </div>
    );
  };

  // Render exam taking screen
  const renderExam = () => (
    <div style={{ padding: '20px', marginTop: '20px' }}>
      {/* Timer bar */}
      <div style={{
        position: 'sticky',
        top: 0,
        backgroundColor: '#1f2937',
        padding: '15px',
        borderRadius: '8px',
        marginBottom: '20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        border: timerExpired ? '2px solid #ef4444' : '2px solid #374151'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <span style={{ color: '#fff', fontSize: '18px', fontWeight: 'bold' }}>
            📝 Practice Exam
          </span>
          <span style={{ color: '#9ca3af' }}>
            {Object.keys(answers).length} / {questions.length} answered
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <span style={{ 
            color: timerExpired ? '#ef4444' : timeRemaining < 300 ? '#fbbf24' : '#10b981',
            fontSize: '24px',
            fontWeight: 'bold'
          }}>
            ⏱️ {formatTime(timeRemaining)}
            {timerExpired && <span style={{ fontSize: '14px', marginLeft: '10px' }}>(Time's up! You can still finish)</span>}
          </span>
          <button
            onClick={submitExam}
            style={{
              padding: '10px 25px',
              backgroundColor: '#3b82f6',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            Submit Exam
          </button>
        </div>
      </div>

      {/* Questions */}
      {questions.map((q, idx) => (
        <div 
          key={idx}
          style={{
            backgroundColor: '#1f2937',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '20px',
            border: answers[idx] ? '2px solid #10b981' : '2px solid #374151'
          }}
        >
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            marginBottom: '15px' 
          }}>
            <span style={{ 
              color: q.type === 'coding' ? '#a78bfa' : '#60a5fa',
              fontSize: '14px',
              fontWeight: 'bold'
            }}>
              {q.type === 'coding' ? '💻 CODING' : '📋 MCQ'} • {q.topic} • {q.difficulty}
            </span>
            <span style={{ color: '#9ca3af' }}>Q{idx + 1}</span>
          </div>

          <div style={{ 
            color: '#fff', 
            fontSize: '16px', 
            marginBottom: '20px',
            lineHeight: '1.6'
          }}>
            <FormattedQuestion text={q.question} />
          </div>

          {q.type === 'mcq' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {q.options.map((option, optIdx) => {
                const optionLetter = option.charAt(0);
                const isSelected = answers[idx] === optionLetter;
                return (
                  <button
                    key={optIdx}
                    onClick={() => handleMCQAnswer(idx, optionLetter)}
                    style={{
                      padding: '12px 15px',
                      backgroundColor: isSelected ? 'rgba(59, 130, 246, 0.3)' : '#374151',
                      border: isSelected ? '2px solid #3b82f6' : '2px solid transparent',
                      borderRadius: '8px',
                      color: '#fff',
                      textAlign: 'left',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    {option}
                  </button>
                );
              })}
            </div>
          ) : (
            <textarea
              value={answers[idx] || ''}
              onChange={(e) => handleCodeAnswer(idx, e.target.value)}
              placeholder="Write your Python code here..."
              style={{
                width: '100%',
                minHeight: '150px',
                padding: '15px',
                backgroundColor: '#111827',
                border: '2px solid #374151',
                borderRadius: '8px',
                color: '#10b981',
                fontFamily: 'monospace',
                fontSize: '14px',
                resize: 'vertical'
              }}
            />
          )}
        </div>
      ))}

      {/* Bottom submit button */}
      <div style={{ textAlign: 'center', marginTop: '30px' }}>
        <button
          onClick={submitExam}
          style={{
            padding: '15px 50px',
            fontSize: '18px',
            backgroundColor: '#3b82f6',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: 'bold'
          }}
        >
          ✅ Submit Exam
        </button>
      </div>
    </div>
  );

  // Render grading screen
  const renderGrading = () => (
    <div style={{ 
      padding: '40px', 
      textAlign: 'center',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '400px'
    }}>
      <div style={{ 
        fontSize: '60px', 
        marginBottom: '20px',
        animation: 'pulse 1.5s ease-in-out infinite'
      }}>✅</div>
      <h2 style={{ color: '#00d4aa', marginBottom: '20px' }}>Grading Your Exam...</h2>
      <p style={{ color: '#9ca3af' }}>AI is reviewing your answers</p>
      <div style={{ 
        marginTop: '30px',
        width: '200px',
        height: '4px',
        backgroundColor: '#374151',
        borderRadius: '2px',
        overflow: 'hidden'
      }}>
        <div style={{
          width: '100%',
          height: '100%',
          backgroundColor: '#10b981',
          animation: 'loading 1.5s ease-in-out infinite'
        }} />
      </div>
    </div>
  );

  // Session-only: ask GPT-4o-mini for a deeper explanation of one graded question.
  // Read-only — does not touch grading, scoring, or any DB write. Scoped to studied concepts.
  const handleExtraExplanation = async (idx, q, result) => {
    if (explainingIdx !== null) return; // one at a time
    setExplainingIdx(idx);
    try {
      const optionsText = (q.options && q.options.length > 0)
        ? `\nOptions:\n${q.options.join('\n')}`
        : '';
      const studentAnswer = answers[idx] || '(no answer)';
      const correctAnswer = result.correctAnswer || q.correctAnswer || q.sampleSolution || '';
      const wasCorrect = result.correct ? 'correct' : 'incorrect';
      const scopeHint = (allowedConceptsForGrading && allowedConceptsForGrading.length > 0)
        ? `\n\nSTAY IN SCOPE: Only use these Python concepts the student has studied: ${allowedConceptsForGrading.join(', ')}. Do not introduce concepts outside this list.`
        : '';

      const res = await apiFetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            {
              role: 'system',
              content: `You are a friendly Python tutor helping a student review a practice-exam question they just completed. Explain clearly WHY the correct answer is correct, and if the student answered incorrectly, gently explain their mistake. Be concise (2-4 short paragraphs). Use markdown code blocks (\`\`\`python) for any code. Do NOT merely restate the answer — explain the underlying concept so they truly understand it.${scopeHint}`
            },
            {
              role: 'user',
              content: `Question (${q.topic || 'Python'}):\n${q.question}${optionsText}\n\nCorrect answer: ${correctAnswer}\nStudent's answer: ${studentAnswer}\nThe student was ${wasCorrect}.\n\nPlease explain this question in more depth so the student understands the concept.`
            }
          ],
          temperature: 0.5,
          max_tokens: 500
        })
      });

      const data = await res.json();
      const explanation = data.choices?.[0]?.message?.content?.trim();
      setExtraExplanations(prev => ({
        ...prev,
        [idx]: explanation || '⚠️ Could not generate an explanation. Please try again.'
      }));
    } catch (err) {
      setExtraExplanations(prev => ({ ...prev, [idx]: '⚠️ Error generating explanation. Please try again.' }));
    } finally {
      setExplainingIdx(null);
    }
  };

  // Render results screen
  const renderResults = () => {
    if (!results) return null;
    
    const percentage = Math.round(results.percentage || 0);
    const scoreColor = percentage >= 80 ? '#10b981' : percentage >= 60 ? '#fbbf24' : '#ef4444';
    
    return (
      <div style={{ padding: '20px' }}>
        {/* Score summary */}
        <div style={{
          backgroundColor: '#1f2937',
          borderRadius: '16px',
          padding: '30px',
          marginBottom: '30px',
          textAlign: 'center',
          border: `3px solid ${scoreColor}`
        }}>
          <div style={{ fontSize: '60px', marginBottom: '10px' }}>
            {percentage >= 80 ? '🎉' : percentage >= 60 ? '👍' : '📚'}
          </div>
          <h2 style={{ color: scoreColor, fontSize: '48px', margin: '0 0 10px 0' }}>
            {percentage}%
          </h2>
          <p style={{ color: '#fff', fontSize: '20px', margin: 0 }}>
            {results.totalScore} / {results.maxScore} points
          </p>
          <p style={{ color: '#9ca3af', marginTop: '15px' }}>
            {results.summary}
          </p>
        </div>

        {/* Recommendations */}
        {results.recommendations && results.recommendations.length > 0 && (
          <div style={{
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '30px'
          }}>
            <h3 style={{ color: '#10b981', margin: '0 0 15px 0' }}>📚 Study Recommendations:</h3>
            <ul style={{ margin: 0, paddingLeft: '20px', color: '#10b981' }}>
              {results.recommendations.map((rec, idx) => (
                <li key={idx} style={{ marginBottom: '8px' }}>{rec}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Detailed results */}
        <h3 style={{ color: '#fff', marginBottom: '20px' }}>📋 Detailed Results:</h3>
        {questions.map((q, idx) => {
          // Try to find result by questionIndex, fallback to array position
          const result = results.results?.find(r => r.questionIndex === idx) || results.results?.[idx] || {};
          const isCorrect = result.correct;
          
          return (
            <div 
              key={idx}
              style={{
                backgroundColor: '#1f2937',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '15px',
                borderLeft: `4px solid ${isCorrect ? '#10b981' : '#ef4444'}`
              }}
            >
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                marginBottom: '10px'
              }}>
                <span style={{ color: '#9ca3af' }}>
                  Q{idx + 1} • {q.topic}
                </span>
                <span style={{ 
                  color: isCorrect ? '#10b981' : '#ef4444',
                  fontWeight: 'bold'
                }}>
                  {isCorrect ? '✓ Correct' : '✗ Incorrect'} 
                  ({result.pointsEarned || 0} pts)
                </span>
              </div>
              
              <div style={{ color: '#fff', marginBottom: '10px' }}><FormattedQuestion text={q.question} /></div>
              
              {/* Your answer */}
              <div style={{ 
                backgroundColor: '#111827', 
                padding: '10px 15px', 
                borderRadius: '8px',
                marginBottom: '10px'
              }}>
                {q.type === 'coding' ? (
                  <>
                    <span style={{ color: '#9ca3af', display: 'block', marginBottom: '5px' }}>Your answer: </span>
                    {answers[idx] ? (
                      <SyntaxHighlighter
                        language="python"
                        style={vscDarkPlus}
                        customStyle={{
                          margin: 0,
                          padding: '12px 16px',
                          borderRadius: '6px',
                          fontSize: '14px',
                          lineHeight: '1.5',
                          border: `1px solid ${isCorrect ? '#10b981' : '#ef4444'}`
                        }}
                        showLineNumbers={false}
                      >
                        {answers[idx]}
                      </SyntaxHighlighter>
                    ) : (
                      <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>(No answer)</span>
                    )}
                  </>
                ) : (
                  <>
                    <span style={{ color: '#9ca3af', display: 'block', marginBottom: '8px' }}>
                      {isCorrect ? 'Your answer: ✓' : 'All options:'}
                    </span>
                    {(q.options || []).map((opt, oi) => {
                      const optLetter = opt.charAt(0).toUpperCase();
                      const studentLetter = (answers[idx] || '').charAt(0).toUpperCase();
                      const correctLetter = (q.correctAnswer || '').charAt(0).toUpperCase();
                      const isStudentPick = optLetter === studentLetter;
                      const isCorrectOpt = optLetter === correctLetter;
                      
                      let bg = 'transparent';
                      let border = '1px solid transparent';
                      let color = '#9ca3af';
                      
                      if (isCorrectOpt) {
                        bg = 'rgba(16, 185, 129, 0.15)';
                        border = '1px solid #10b981';
                        color = '#10b981';
                      } else if (isStudentPick && !isCorrect) {
                        bg = 'rgba(239, 68, 68, 0.15)';
                        border = '1px solid #ef4444';
                        color = '#ef4444';
                      }
                      
                      return (
                        <div key={oi} style={{
                          padding: '6px 12px',
                          marginBottom: '4px',
                          borderRadius: '6px',
                          backgroundColor: bg,
                          border,
                          color,
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          {isCorrectOpt && <span>✅</span>}
                          {isStudentPick && !isCorrect && <span>❌</span>}
                          {opt}
                          {isStudentPick && <span style={{ fontSize: '11px', opacity: 0.7 }}>(your pick)</span>}
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
              
              {/* Correct answer - only for coding */}
              {!isCorrect && q.type === 'coding' && (
                <div style={{ 
                  backgroundColor: 'rgba(16, 185, 129, 0.1)', 
                  padding: '10px 15px', 
                  borderRadius: '8px',
                  marginBottom: '10px'
                }}>
                  <span style={{ color: '#9ca3af', display: 'block', marginBottom: '5px' }}>Correct answer: </span>
                  <SyntaxHighlighter
                    language="python"
                    style={vscDarkPlus}
                    customStyle={{
                      margin: 0,
                      padding: '12px 16px',
                      borderRadius: '6px',
                      fontSize: '14px',
                      lineHeight: '1.5',
                      border: '1px solid #30363d'
                    }}
                    showLineNumbers={false}
                  >
                    {formatCode(result.correctAnswer || q.sampleSolution)}
                  </SyntaxHighlighter>
                </div>
              )}
              
              <div style={{ 
                color: '#60a5fa', 
                margin: 0,
                fontStyle: 'italic'
              }}>
                💡 <FormattedQuestion text={result.explanation} />
              </div>

              {/* Explain More — session-only deeper AI explanation */}
              <div style={{ marginTop: '12px' }}>
                <button
                  onClick={() => handleExtraExplanation(idx, q, result)}
                  disabled={explainingIdx !== null}
                  style={{
                    padding: '6px 14px',
                    backgroundColor: explainingIdx === idx ? '#6b7280' : '#7c3aed',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: explainingIdx !== null ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    fontWeight: '600',
                    opacity: (explainingIdx !== null && explainingIdx !== idx) ? 0.5 : 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  title="Get a deeper AI explanation for this question"
                >
                  {explainingIdx === idx ? '🤖 Thinking…' : '🤖 Explain More'}
                </button>

                {extraExplanations[idx] && (
                  <div style={{
                    marginTop: '12px',
                    padding: '14px 16px',
                    backgroundColor: 'rgba(124, 58, 237, 0.1)',
                    border: '1px solid rgba(124, 58, 237, 0.4)',
                    borderRadius: '8px',
                    color: '#e9d5ff'
                  }}>
                    <div style={{ color: '#c4b5fd', fontWeight: '600', marginBottom: '8px', fontSize: '13px' }}>
                      🤖 Deeper Explanation
                    </div>
                    <FormattedQuestion text={extraExplanations[idx]} />
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Action buttons */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          gap: '20px',
          marginTop: '30px'
        }}>
          <button
            onClick={() => {
              setExamState('setup');
              setQuestions([]);
              setAnswers({});
              setResults(null);
            }}
            style={{
              padding: '15px 30px',
              backgroundColor: '#3b82f6',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            🔄 Take Another Exam
          </button>
          <button
            onClick={onClose}
            style={{
              padding: '15px 30px',
              backgroundColor: '#374151',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            Close
          </button>
        </div>
      </div>
    );
  };

  if (isDemoMode()) {
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.9)', zIndex: 1000,
        display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
        paddingTop: '60px', overflowY: 'auto'
      }}>
        <div style={{
          backgroundColor: '#111827', borderRadius: '16px',
          border: '2px solid rgba(97, 218, 251, 0.3)',
          width: '90%', maxWidth: '900px', maxHeight: '90vh',
          overflowY: 'auto', position: 'relative', padding: '48px 40px'
        }}>
          <button onClick={onClose} style={{
            position: 'absolute', top: '10px', right: '10px',
            backgroundColor: '#374151', border: 'none', borderRadius: '8px',
            color: '#f0f6fc', padding: '6px 12px', cursor: 'pointer', fontSize: '14px'
          }}
            onMouseOver={(e) => { e.target.style.backgroundColor = '#f85149'; e.target.style.color = '#ffffff'; }}
            onMouseOut={(e) => { e.target.style.backgroundColor = '#374151'; e.target.style.color = '#f0f6fc'; }}
          >✕</button>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>🎓</div>
          <h2 style={{ color: '#fffacd', marginBottom: '16px', fontSize: '22px' }}>Practice Exam — Locked in Demo Mode</h2>
          <div style={{ color: '#fffacd', whiteSpace: 'pre-wrap', lineHeight: '1.8', fontSize: '14px' }}>
            {`With a real account you can generate full timed practice exams at any time, with questions tailored to your weaknesses.

How it works:
  1. Choose a chapter or let the AI pick based on your weakest topics
  2. Select difficulty: Easy / Medium / Hard / Mixed
  3. The AI generates 13 multiple-choice + 2 short coding questions
  4. A 20-minute countdown timer starts automatically
  5. Submit — MCQs are graded instantly, coding answers are reviewed by the AI

Example exam questions:

  Q1 (MCQ): What is the output of the following code?
       for i in range(2, 6):
           print(i)
  A) 2 3 4 5   B) 2 3 4 5 6   C) 1 2 3 4 5   D) 2 4 6

  Q14 (Coding): Write a function that takes a list of numbers and
       returns only the even ones.

After submission you get:
  ✅  Score and per-question breakdown
  💬  AI explanation for every wrong answer
  📊  Results saved to your Quiz Analytics dashboard

The personalized mode weights questions toward your weak spots so
you spend exam time where it matters most.

✨ Sign up to take your first practice exam!`}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
      zIndex: 1000,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      paddingTop: '60px',
      overflowY: 'auto'
    }}>
      <div style={{
        backgroundColor: '#111827',
        borderRadius: '16px',
        border: '2px solid rgba(97, 218, 251, 0.3)',
        width: '90%',
        maxWidth: '900px',
        maxHeight: '90vh',
        overflowY: 'auto',
        position: 'relative'
      }}>
        {/* Close button — goes back to setup if exam is in progress, otherwise closes the panel */}
        <button
          onClick={() => {
            if (examState === 'taking' || examState === 'grading' || examState === 'generating') {
              setExamState('setup');
              setQuestions([]);
              setAnswers({});
              setResults(null);
              setError(null);
              setLoading(false);
              setGenerationStep(0);
            } else {
              onClose();
            }
          }}
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            backgroundColor: '#374151',
            border: 'none',
            borderRadius: '8px',
            color: '#f0f6fc',
            padding: '6px 12px',
            cursor: 'pointer',
            zIndex: 1000,
            fontSize: '14px'
          }}
          onMouseOver={(e) => { e.target.style.backgroundColor = '#f85149'; e.target.style.color = '#ffffff'; }}
          onMouseOut={(e) => { e.target.style.backgroundColor = '#374151'; e.target.style.color = '#f0f6fc'; }}
        >
          ✕
        </button>

        {/* Render appropriate screen */}
        {examState === 'setup' && renderSetup()}
        {examState === 'generating' && renderGenerating()}
        {examState === 'taking' && renderExam()}
        {examState === 'grading' && renderGrading()}
        {examState === 'results' && renderResults()}
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes loading {
          0% { transform: translateX(-100%); }
          50% { transform: translateX(100%); }
          100% { transform: translateX(-100%); }
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.1); opacity: 0.8; }
        }
      `}</style>
    </div>
  );
};

export default PracticeExam;
